/**
 * The prototype design-style catalog.
 *
 * Each entry is a self-contained "design.md" — a markdown design brief the
 * Engineer agent can follow verbatim (palette with hexes, typography, shape
 * language, effects, motion). Designs travel ONLY as issue text: the chosen
 * design's prompt is appended to the prototype's first brief at creation, and
 * a later design change files a restyle issue. Nothing in agent-runtime knows
 * about this module.
 *
 * `preview` carries just enough tokens for the web gallery to render a live
 * swatch card of the style with plain CSS (no images, no font loading).
 */

export type PrototypeDesignCategory =
  | 'modern'
  | 'retro'
  | 'classic'
  | 'editorial'
  | 'product'
  | 'mood';

export const PROTOTYPE_DESIGN_CATEGORIES: Record<PrototypeDesignCategory, string> = {
  modern: 'Modern',
  retro: 'Retro',
  classic: 'Movements',
  editorial: 'Editorial',
  product: 'Product',
  mood: 'Mood',
};

export interface PrototypeDesignPreview {
  /** Page background. */
  bg: string;
  /** Card/surface color. */
  surface: string;
  /** Primary text/ink color. */
  text: string;
  /** Accent / CTA color. */
  accent: string;
  /** Typography direction, mapped to a generic stack by the gallery. */
  font: 'sans' | 'serif' | 'mono' | 'display';
  /** Corner radius in px for the preview swatches. */
  radius: number;
  /** Optional CSS border for surfaces (e.g. '2px solid #111'). */
  border?: string;
  /** Optional CSS box-shadow for surfaces. */
  shadow?: string;
}

export interface PrototypeDesign {
  /** Stable kebab-case id — persisted on Project.designId. Never rename. */
  id: string;
  name: string;
  category: PrototypeDesignCategory;
  /** One-line vibe shown on the gallery card. */
  tagline: string;
  /** The full design.md body (markdown, no top-level heading). */
  prompt: string;
  preview: PrototypeDesignPreview;
}

export function getPrototypeDesign(id: string): PrototypeDesign | undefined {
  return PROTOTYPE_DESIGNS.find((d) => d.id === id);
}

/**
 * Render a design as the markdown block appended to an issue brief. Both the
 * creation brief and later restyle issues compose through this so the
 * Engineer always sees the same shape — including the explicit override of
 * the scaffold's default "light, clean, neutral" AGENTS.md guidance, without
 * which dark or loud styles would lose to the default.
 */
export function formatDesignDirection(design: PrototypeDesign): string {
  return [
    `## Design direction — ${design.name}`,
    '',
    'This design direction is the visual contract for the whole app. It OVERRIDES the default styling guidance in AGENTS.md (including its light-theme default) — follow it for the theme tokens in `client/src/index.css`, the `client/src/components/ui/` primitives, and every page you build.',
    '',
    design.prompt.trim(),
  ].join('\n');
}

/**
 * The design block appended to a prototype's first brief when no catalog style
 * was picked (the app's "no style" option, or an Integration API create without
 * `designId`). Without it the Engineer copied the scaffold's neutral placeholder
 * look and every prototype came out the same generic slate-on-white. Instead
 * the Engineer chooses a direction that fits the brief, records it in
 * `DESIGN.md` so later iterations stay consistent, and applies it everywhere.
 * The catalog names are listed as a menu of the level of specificity we want.
 */
export function formatAutoDesignDirection(): string {
  const menu = (Object.keys(PROTOTYPE_DESIGN_CATEGORIES) as PrototypeDesignCategory[])
    .map((category) => {
      const names = PROTOTYPE_DESIGNS.filter((d) => d.category === category).map((d) => d.name);
      return `- ${PROTOTYPE_DESIGN_CATEGORIES[category]}: ${names.join(', ')}`;
    })
    .join('\n');
  return [
    '## Design direction — choose one that fits this brief',
    '',
    'No design style was picked for this prototype, so choosing one is part of the job. This block OVERRIDES the default styling guidance in AGENTS.md (including its light-theme default): the scaffold\'s neutral slate-on-white look is a placeholder, not a style — do NOT ship it, and do not ship any other generic template look.',
    '',
    '1. Read the brief above and decide who uses this app, where, and how it should feel. Let the domain drive the look: e.g. a kids\' reading tracker → playful, bright, rounded; a law-firm intake tool → restrained editorial serif; a gym log → bold, high-contrast, energetic; a bakery shop → warm, crafted, appetizing.',
    '2. Pick ONE distinctive direction — a style from the menu below, or an equally specific one of your own. Light or dark is your call: choose what suits the brief (no theme toggle unless the brief asks for one).',
    '3. Write it down as `DESIGN.md` at the repo root with: **Palette** (exact hexes for canvas, surface, ink, and ONE accent plus what the accent is reserved for), **Typography** (a heading + body pairing with fallbacks), **Shape** (radius, borders, shadow policy), **Texture/Effects**, **Motion**, and **Rules** (explicit "never" rules). Every later change follows `DESIGN.md`.',
    '4. Apply it to the whole app: the theme tokens in `client/src/index.css`, the `client/src/components/ui/` primitives, the admin shell, and every page you build.',
    '',
    'Usability rules still hold whatever the style: `lucide-react` icons (never emoji), mobile-first responsive layouts, readable contrast, and visible hover/focus/disabled states.',
    '',
    'Style menu:',
    menu,
  ].join('\n');
}

export const PROTOTYPE_DESIGNS: PrototypeDesign[] = [
  {
    id: 'neo-brutalism',
    name: 'Neo-Brutalism',
    category: 'modern',
    tagline: 'Loud color, hard edges, zero apology.',
    prompt:
      'Palette: warm cream canvas #FDF6E3, pure white cards, sticker brights #FFDE59 yellow, #FF6B6B coral, #A388EE violet, with ink always pure #000000. Typography: Archivo Black or Space Grotesk (fallback: heavy grotesque sans) at 700-900 for oversized headings, Inter for body. Shape: 0-4px radius, 3px solid #000000 borders on every card, button, and input; hard offset shadows 5px 5px 0 #000000 with zero blur. Texture/Effects: dead-flat fills only; an occasional hand-drawn star or squiggle doodle is the sole ornament. Motion: 120ms snappy transforms; hover presses the element 3px into its shadow, active collapses it flat. Rules: never use gradients or blur; never soften a shadow; coral #FF6B6B is reserved exclusively for primary CTAs; every visible edge gets a black border — no borderless surfaces, ever.',
    preview: {
      bg: '#FDF6E3',
      surface: '#FFFFFF',
      text: '#111111',
      accent: '#FF6B6B',
      font: 'display',
      radius: 2,
      border: '3px solid #000000',
      shadow: '5px 5px 0 #000000',
    },
  },
  {
    id: 'glassmorphism',
    name: 'Glassmorphism',
    category: 'modern',
    tagline: 'Frosted panels adrift on a color storm.',
    prompt:
      'Palette: a vivid #4158D0 to #C850C0 diagonal gradient fills the page; panels are white glass rgba(255,255,255,0.15); text near-white #F8F9FF; accent magenta #C850C0 pulled from the backdrop. Typography: Poppins for headings, Inter for body (fallback: clean geometric sans), comfortable 1.6 line-height over blur. Shape: 20px radius cards with backdrop-filter blur(16px), a 1px rgba(255,255,255,0.35) top-lit border, soft diffuse shadow 0 8px 32px rgba(0,0,0,0.25). Texture/Effects: two or three large blurred color blobs drift behind the glass; frost intensifies one step on modals. Motion: gentle 400ms parallax of background blobs on scroll, nothing faster. Rules: glass only works over color — never place a glass panel on a flat white ground; never stack more than two glass layers; keep borders 1px and translucent, no opaque outlines; body text always solid, never translucent.',
    preview: {
      bg: '#4158D0',
      surface: 'rgba(255,255,255,0.18)',
      text: '#F8F9FF',
      accent: '#C850C0',
      font: 'sans',
      radius: 20,
      border: '1px solid rgba(255,255,255,0.35)',
      shadow: '0 8px 32px rgba(0,0,0,0.25)',
    },
  },
  {
    id: 'claymorphism',
    name: 'Claymorphism',
    category: 'modern',
    tagline: 'Squeezable pastel clay, inflated and kind.',
    prompt:
      'Palette: soft #F5F3FF lilac-white ground, white clay surfaces, saturated pastels #A78BFA lilac, #FDA4AF pink, #93C5FD baby blue; ink is gentle #3F3D56. Typography: Nunito or Baloo 2 (fallback: rounded friendly sans), 700 headings, generous sizes. Shape: everything inflated — 28-36px radius, zero borders; the signature triple shadow: a big soft tinted outer glow 0 16px 32px rgba(167,139,250,0.35), an inset dark bottom-right, an inset white top-left, so each card reads as squeezable clay. Texture/Effects: matte, toy-like fills; no gradients beyond the shadow modelling. Motion: bouncy spring scale to 0.96 on press, 250ms overshoot on release. Rules: no sharp corners anywhere — 24px radius is the minimum; never use plain black shadows, always tint them with the surface hue; lilac #A78BFA is reserved for interactive elements; keep ink soft — never pure #000.',
    preview: {
      bg: '#F5F3FF',
      surface: '#FFFFFF',
      text: '#3F3D56',
      accent: '#A78BFA',
      font: 'sans',
      radius: 28,
      shadow: '0 16px 32px rgba(167,139,250,0.35)',
    },
  },
  {
    id: 'soft-ui',
    name: 'Soft UI',
    category: 'modern',
    tagline: 'One surface, sculpted entirely by light.',
    prompt:
      'Palette: one desaturated ground #E0E5EC does everything — page, cards, and buttons are the same color, extruded by light; ink #2D3436; a single sparse accent #4B70E2. Typography: Poppins at 300-500 (fallback: light geometric sans); labels small, airy, letterspaced. Shape: 14-18px radius, absolutely no borders; the twin-shadow signature — light source top-left: 6px 6px 12px #A3B1C6 paired with -6px -6px 12px #FFFFFF; pressed and active states flip both shadows inset. Texture/Effects: matte plastic; nothing else — no gradients, no imagery competing with the relief. Motion: 200ms ease on the shadow flip; controls should feel like physical toggles. Rules: surfaces must never differ in color from the background — depth comes only from the twin shadows; blue #4B70E2 appears exclusively on the active or interactive state; never add a border; never use this style for text-dense pages without raising ink contrast.',
    preview: {
      bg: '#E0E5EC',
      surface: '#E0E5EC',
      text: '#2D3436',
      accent: '#4B70E2',
      font: 'sans',
      radius: 16,
      shadow: '6px 6px 12px #A3B1C6, -6px -6px 12px #FFFFFF',
    },
  },
  {
    id: 'bento-grid',
    name: 'Bento Grid',
    category: 'modern',
    tagline: 'A keynote of tidy, mixed-size tiles.',
    prompt:
      'Palette: quiet #F5F5F7 ground, pure white tiles, #1D1D1F ink, one accent per tile drawn from #0071E3 blue and #30D158 green — never both in the same tile. Typography: Inter Tight (fallback: tight-tracked display sans); huge stat numerals at 48-80px weight 700 with -2% tracking, small muted captions beneath. Shape: a bento of mixed-span tiles on a 12-column grid, uniform 20px radius, 14px gutters, 1px #E8E8ED hairline borders; one hero tile spans double. Texture/Effects: flat surfaces; a single tile may carry a soft tinted gradient wash as the focal point. Motion: staggered 60ms fade-up on load, 2px hover lift with shadow 0 8px 24px rgba(0,0,0,0.08). Rules: every piece of content lives in a tile — no floating text between tiles; keep radius and gutters identical across all tiles; never exceed one accent hue per tile; no heavy drop shadows at rest.',
    preview: {
      bg: '#F5F5F7',
      surface: '#FFFFFF',
      text: '#1D1D1F',
      accent: '#0071E3',
      font: 'sans',
      radius: 20,
      border: '1px solid #E8E8ED',
    },
  },
  {
    id: 'midnight-saas',
    name: 'Midnight SaaS',
    category: 'modern',
    tagline: 'Midnight precision instrument.',
    prompt:
      'Palette: near-black canvas #08090A (never pure #000), raised surfaces #101113 and #16171A, text #E6E6E6 with #8A8F98 secondary, one chromatic accent only: #5E6AD2 lavender, plus faint radial glows of it at 5% opacity. Typography: Inter at 13-14px for UI (fallback: neutral grotesque), tight leading, tabular numerals in every table, 500-weight headings — nothing heavier. Shape: 6-8px radius, 1px rgba(255,255,255,0.08) borders; hierarchy comes from surface lightness steps, not shadows. Texture/Effects: subtle grain inside hero glows; otherwise flawless flat darkness. Motion: 100-150ms ease-out everywhere; command-palette scale-fade at 120ms; nothing theatrical. Rules: lavender #5E6AD2 is the only saturated color in the product — reserve it for primary actions and focus rings; no gradients on UI chrome; no light mode; never use pure white text; density is the aesthetic — resist padding.',
    preview: {
      bg: '#08090A',
      surface: '#101113',
      text: '#E6E6E6',
      accent: '#5E6AD2',
      font: 'sans',
      radius: 8,
      border: '1px solid rgba(255,255,255,0.08)',
    },
  },
  {
    id: 'mono-engineering',
    name: 'Mono Engineering',
    category: 'modern',
    tagline: 'Black-and-white blueprint discipline.',
    prompt:
      'Palette: pure #000000 and #FFFFFF with a disciplined grey ramp (#666666 secondary, #EAEAEA hairlines, #FAFAFA wells); rare accents #0070F3 blue for links and #FF0080 pink for highlights. Typography: Geist-style pairing — Inter for UI plus JetBrains Mono (fallback: neutral sans + engineering mono) for code, paths, and metrics; letterspaced 11px uppercase mono labels. Shape: 6px radius, 1px #EAEAEA borders on everything, stacked micro-shadows (0 1px 2px and 0 2px 8px at low alpha) — never one heavy drop. Texture/Effects: blueprint dot grids and hairline rules at 5-8% opacity as the only decoration. Motion: instant state changes; skeleton shimmer is the sole animation. Rules: black IS the conversion color — primary buttons are solid #000 on white; never use font-weight 700; no gradients; color appears only when it encodes meaning (links, errors, diffs).',
    preview: {
      bg: '#FFFFFF',
      surface: '#FAFAFA',
      text: '#000000',
      accent: '#0070F3',
      font: 'mono',
      radius: 6,
      border: '1px solid #EAEAEA',
    },
  },
  {
    id: 'mesh-gradient',
    name: 'Mesh Gradient',
    category: 'modern',
    tagline: 'One spectacular gradient, then silence.',
    prompt:
      'Palette: calm white body with deep #0A2540 navy ink and #425466 secondary; the hero carries one spectacular diagonal mesh of #635BFF indigo, #00D4FF cyan, #FF5996 pink, and #FFC848 gold. Typography: Inter (fallback: refined neo-grotesque) at calm 400-600 weights; enable tabular numerals on every money or metric cell; display weight never above 600. Shape: 8-12px radius, hairline #E6EBF1 borders, soft layered shadows 0 6px 18px rgba(50,50,93,0.11); the hero section ends on a -6deg skewed edge. Texture/Effects: the mesh gradient is the ONLY decoration and lives at hero scale only — everything below the fold is whitespace and type. Motion: the mesh drifts imperceptibly slowly; content reveals on scroll at 300ms. Rules: never repeat the gradient in cards, buttons, or footers; indigo #635BFF never appears as body text; keep buttons solid, not gradient-filled; whitespace does the persuading.',
    preview: {
      bg: '#FFFFFF',
      surface: '#F6F9FC',
      text: '#0A2540',
      accent: '#635BFF',
      font: 'sans',
      radius: 10,
      shadow: '0 6px 18px rgba(50,50,93,0.11)',
    },
  },
  {
    id: 'aurora-glow',
    name: 'Aurora Glow',
    category: 'modern',
    tagline: 'Northern lights behind dark glass.',
    prompt:
      'Palette: near-black void #06060F to #0A0A1A, with huge blurred aurora blobs of #7F5AF0 purple, #2CB67D teal, and #FF8FAB pink glowing behind content; text #EDEDF7 on glass cards rgba(255,255,255,0.04). Typography: Inter or General Sans (fallback: modern grotesque); display headlines may be gradient-clipped through the aurora hues — body text never is. Shape: 16px radius glass cards with backdrop blur and a 1px rgba(255,255,255,0.10) border; no opaque panels. Texture/Effects: layered radial gradients plus a fine grain overlay at 3% so the glow never bands. Motion: blobs drift on a 30-60s loop, far below conscious speed; card hovers brighten the border, not the fill. Rules: auroras stay behind the glass — never place saturated color inside a card; one gradient-clipped headline per page maximum; never use pure black or pure white; no hard shadows in this universe.',
    preview: {
      bg: '#06060F',
      surface: 'rgba(255,255,255,0.05)',
      text: '#EDEDF7',
      accent: '#7F5AF0',
      font: 'sans',
      radius: 16,
      border: '1px solid rgba(255,255,255,0.10)',
    },
  },
  {
    id: 'material-tonal',
    name: 'Material Tonal',
    category: 'modern',
    tagline: 'Color grown from a single seed.',
    prompt:
      'Palette: tonal ramps grown from seed #6750A4 — surface #FFFBFE, primary container #EADDFF with on-container ink #21005D, primary #6750A4, and a warm tertiary #7D5260 for secondary emphasis. Typography: Roboto Flex (fallback: variable grotesque sans) with weight that morphs by role — 400 body, 500 labels, headlines relaxing to 400 at large sizes. Shape: expressive corner tokens — small controls 12px, cards 16px, sheets and heroes 28px; buttons are full pills; feature imagery sits in squircle masks. Texture/Effects: elevation is a tint shift toward the primary hue, not a shadow — higher surfaces are simply more colored. Motion: spring physics on everything; containers shape-morph between states rather than crossfade. Rules: every color must come from the tonal ramps — no hex outside the system; no grey shadows; never mix radius tokens within one component class; state layers (8% and 12% overlays) handle hover — never darken by arbitrary amounts.',
    preview: {
      bg: '#FFFBFE',
      surface: '#EADDFF',
      text: '#21005D',
      accent: '#6750A4',
      font: 'sans',
      radius: 24,
    },
  },
  {
    id: 'flat-friendly',
    name: 'Friendly Flat',
    category: 'modern',
    tagline: 'Cheerful, legible, impossible to fear.',
    prompt:
      'Palette: white ground, ink #202124, friendly saturated primaries #4285F4 blue, #34A853 green, #FBBC05 amber, #EA4335 red — blue leads, the others appear only as semantic status colors. Typography: Roboto or Open Sans (fallback: humanist sans), weights 400 and 500 only, 16px body, an unfussy scale. Shape: 8px radius, borderless cards on white, a single soft shadow 0 2px 4px rgba(0,0,0,0.1); primary buttons may carry a subtle 2-stop vertical gradient for affordance. Texture/Effects: flat illustration spots welcome; photography framed plainly; nothing textured. Motion: 200ms ease-in-out; a ripple on tap; no parallax. Rules: exactly one shadow recipe app-wide — never stack or enlarge it; green, amber, and red are status-only, never decoration; keep it relentlessly legible — no type below 12px; never use more than two weights on a screen.',
    preview: {
      bg: '#FFFFFF',
      surface: '#F8F9FA',
      text: '#202124',
      accent: '#4285F4',
      font: 'sans',
      radius: 8,
      shadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
  },
  {
    id: 'pastel-blush',
    name: 'Pastel Blush',
    category: 'modern',
    tagline: 'Airy pastels that whisper, never shout.',
    prompt:
      'Palette: blush #FFE5EC ground, white cards, powder blue #E0F4FF and mint #E8FFE0 as section tints, soft plum ink #5C5470, rose accent #EC7FA9. Typography: DM Sans or Quicksand (fallback: soft geometric sans) at light 300-500 weights; headings airy, never bold. Shape: pills and 24px+ radii everywhere; cards are borderless and float on tinted grounds with whisper shadows 0 8px 24px rgba(92,84,112,0.08). Texture/Effects: large blurred pastel washes behind heroes; delicate hairlines in #F3D9E2 when separation is unavoidable. Motion: floaty — 400-600ms ease, elements drift up 8px on reveal; nothing snaps. Rules: rose #EC7FA9 is reserved exclusively for interactive elements; never use pure black or pure white text; no borders heavier than 1px; no sharp corners anywhere; if a screen feels busy, remove an element rather than shrink one.',
    preview: {
      bg: '#FFE5EC',
      surface: '#FFFFFF',
      text: '#5C5470',
      accent: '#EC7FA9',
      font: 'sans',
      radius: 24,
      shadow: '0 8px 24px rgba(92,84,112,0.08)',
    },
  },
  {
    id: 'liquid-glass',
    name: 'Liquid Glass',
    category: 'modern',
    tagline: 'Interface poured through a wet lens.',
    prompt:
      'Palette: a vibrant deep backdrop (#16233A navy with teal glints) with content on refractive glass rgba(255,255,255,0.12); text #FFFFFF, controls tinted by whatever sits behind them; accent system blue #0A84FF. Typography: SF-adjacent — Inter with optical sizing (fallback: high-legibility neutral sans); weights 400-600, generous sizes for legibility over blur. Shape: capsules and squircles for everything; nested elements keep concentric radii (outer 28px, inner 20px); edges are defined by a 1px specular rim rgba(255,255,255,0.4) and refraction, never by opaque borders. Texture/Effects: heavy backdrop blur with subtle lensing distortion at panel edges; a moving specular highlight along the top rim. Motion: gel physics — controls wobble subtly on touch and morph between shapes rather than swap. Rules: no flat opaque surfaces in the chrome layer; never draw a hard border; text must pass contrast over the busiest possible backdrop; one material everywhere — do not mix glass recipes.',
    preview: {
      bg: '#16233A',
      surface: 'rgba(255,255,255,0.12)',
      text: '#FFFFFF',
      accent: '#0A84FF',
      font: 'sans',
      radius: 28,
      border: '1px solid rgba(255,255,255,0.4)',
      shadow: '0 12px 32px rgba(0,0,0,0.35)',
    },
  },
  {
    id: 'kinetic-type',
    name: 'Kinetic Type',
    category: 'modern',
    tagline: 'The typography is the architecture.',
    prompt:
      'Palette: stark two-tone — ink #111111 on warm bone #F2F0EA; a single signal orange #FF4D00 exists solely for the one moving element per viewport. Typography: typography IS the interface — a variable display grotesque like Unbounded or Archivo (fallback: ultra-wide display sans) set at viewport scale (10-18vw headlines), with tiny 11px mono captions as counterpoint. Shape: no cards, no boxes — text blocks are the structure; full-bleed marquee strips act as section dividers; zero radius, zero shadows, zero gradients. Texture/Effects: none; the letterforms carry everything. Motion: scroll-linked — headlines skew with scroll velocity, letters stagger in 20ms apart, marquees loop infinitely at a walking pace; motion is the decoration budget, spend it nowhere else. Rules: maximum two font weights per screen; never shrink the display type to fit — let it crop; orange appears only once per viewport; no imagery unless it sits behind type.',
    preview: {
      bg: '#F2F0EA',
      surface: '#F2F0EA',
      text: '#111111',
      accent: '#FF4D00',
      font: 'display',
      radius: 0,
    },
  },
  {
    id: 'holo-foil',
    name: 'Holo Foil',
    category: 'modern',
    tagline: 'Oil-slick sheen on gallery white.',
    prompt:
      'Palette: near-white #F7F7FB ground, white cards, ink #1B1B24; the signature is an iridescent oil-slick ramp #B5A9FF to #A9FFF7 to #FFB3DE used for foil edges and clipped display text. Typography: Space Grotesk for display (fallback: sleek extended sans), Inter for body; hero headlines gradient-clipped through the holo ramp. Shape: clean 14px-radius cards with 1px holo-gradient borders; occasional chrome blob shapes as background ornaments; everything else restrained and minimal. Texture/Effects: the foil must move — gradient angle shifts with cursor position for a tilt-card sheen; soft lavender shadows 0 8px 24px rgba(181,169,255,0.35). Motion: 200ms tilt response, slow 8s hue drift when idle. Rules: iridescence is the only color in the system — all other UI stays greyscale; never clip body text through the gradient; one holo hero element per page; shadows are always tinted lavender, never grey or black.',
    preview: {
      bg: '#F7F7FB',
      surface: '#FFFFFF',
      text: '#1B1B24',
      accent: '#B5A9FF',
      font: 'sans',
      radius: 14,
      border: '1px solid #DCD7FF',
      shadow: '0 8px 24px rgba(181,169,255,0.35)',
    },
  },
  {
    id: 'sticker-pop',
    name: 'Sticker Pop',
    category: 'modern',
    tagline: 'A fresh sheet of die-cut stickers.',
    prompt:
      'Palette: loud grounds — cyan #31C4F3 or sunshine #FFD91A — with sticker elements in #FF5C8A pink, #9B5DE5 purple, and white; ink #231A00 deep brown-black. Typography: Baloo 2 (fallback: chunky rounded display) for headings, often with a white outline stroke; Nunito for body. Shape: every card, button, and badge is a die-cut sticker: 4-6px solid #FFFFFF stroke, 16-20px radius, soft drop shadow 0 4px 10px rgba(0,0,0,0.18), and a random plus-or-minus 3deg rotation; elements overlap deliberately. Texture/Effects: glossy sheen highlights, an occasional peeling corner on featured cards. Motion: slap-on entrances that overshoot and settle, gentle jiggle on hover, 250ms springs. Rules: nothing sits flat — every element gets the white die-cut stroke, no exceptions; keep rotations under 4 degrees; never use thin or elegant type; grounds stay saturated — white backgrounds are for stickers, not pages.',
    preview: {
      bg: '#31C4F3',
      surface: '#FFD91A',
      text: '#231A00',
      accent: '#FF5C8A',
      font: 'display',
      radius: 18,
      border: '4px solid #FFFFFF',
      shadow: '0 4px 10px rgba(0,0,0,0.18)',
    },
  },
  {
    id: 'dopamine-max',
    name: 'Dopamine Max',
    category: 'modern',
    tagline: 'Every color, all at once, on purpose.',
    prompt:
      'Palette: maximum-joy clashes — #FF3EA5 pink, #FFDB00 yellow, #01DDDD cyan, #7C4DFF violet, #FF6D00 orange, colliding on purpose; ink is deep #1A0033. Typography: a fat display face like Archivo Black or Lexend Mega (fallback: ultra-bold geometric sans) for headlines, Inter 500 for body so content survives the noise. Shape: big friendly forms — 20px radius cards, 3px solid #1A0033 outlines, chunky pill buttons; sections alternate background hues rather than sharing one. Texture/Effects: flat, saturated fills; checkerboards, stars, and blob shapes as ornaments; no gradients — color blocks do the work. Motion: playful and constant but small: 150ms bounces, wiggling icons, confetti on success states. Rules: every section must change background color — monochrome stretches are forbidden; text sits only on hues that keep strong contrast with #1A0033; never desaturate; keep body copy short — this style shouts, it does not lecture.',
    preview: {
      bg: '#FFDB00',
      surface: '#FFFFFF',
      text: '#1A0033',
      accent: '#FF3EA5',
      font: 'display',
      radius: 20,
      border: '3px solid #1A0033',
    },
  },
  {
    id: 'y2k-chrome',
    name: 'Y2K Chrome',
    category: 'retro',
    tagline: 'Liquid chrome dreaming of the millennium.',
    prompt:
      'Palette: deep space violet #2E0854 ground, liquid-metal silvers #C0C0C0 to #F8F8FF for chrome surfaces, hot pink #FF6EC7 and aqua #01F9C6 as cyber accents; ink #1A1030 on metal. Typography: bubble or chrome-effect display for headlines (try Bungee or Audiowide; fallback: inflated Y2K display), a squarish Eurostile-like sans (Michroma) for body and labels. Shape: blobby capsules and beveled buttons with specular vertical gradients; 24px+ radius; wireframe globes and perspective grids as background ornament. Texture/Effects: lens flares, holographic foil patches, a starfield sparkle on the hero; every metal surface needs a bright specular band. Motion: logo spin-ins, diagonal shine sweeps across chrome every few seconds, 300ms elastic hovers. Rules: chrome is never flat grey — always gradient with a highlight; pink #FF6EC7 is reserved for CTAs; no minimalism — empty space should get a grid or a globe; never use serif faces.',
    preview: {
      bg: '#2E0854',
      surface: '#E4E4F0',
      text: '#1A1030',
      accent: '#FF6EC7',
      font: 'display',
      radius: 24,
      border: '1px solid #F8F8FF',
      shadow: '0 0 12px rgba(255,110,199,0.5)',
    },
  },
  {
    id: 'vaporwave',
    name: 'Vaporwave',
    category: 'retro',
    tagline: 'A mall at midnight in 1987, forever.',
    prompt:
      'Palette: sunset navy #2D1B69 ground, neon pastel quartet #FF71CE pink, #01CDFE cyan, #B967FF purple, #05FFA1 mint, plus pale #FFFB96 for glow text; primary text #F4EFFF. Typography: serif italics for romantic display lines (Playfair Display Italic), wide-tracked all-caps sans for labels, and full-width letterspacing on hero words (fallback: classical serif plus spaced grotesque). Shape: floating collage layers — window-chrome frames with title bars, marble-bust and palm silhouettes, a pink horizon grid receding at the base of the hero; sharp 2px radius frames. Texture/Effects: VHS scanlines at 10% over heroes, checkerboard floors, soft chromatic aberration on display text. Motion: slow dreamy zooms, an occasional 2-frame glitch stutter; nothing urgent. Rules: cyan #01CDFE outlines the frames, pink #FF71CE fills the CTAs — never swap them; every hero needs a horizon; no pure white, ever — tint it lavender; irony is the tone, so never use corporate stock imagery.',
    preview: {
      bg: '#2D1B69',
      surface: '#241458',
      text: '#F4EFFF',
      accent: '#FF71CE',
      font: 'serif',
      radius: 2,
      border: '1px solid #01CDFE',
    },
  },
  {
    id: 'synthwave',
    name: 'Synthwave',
    category: 'retro',
    tagline: 'Neon sunset over an endless grid.',
    prompt:
      'Palette: night gradient from #1F1030 to twilight purple #461E52, a sunset band of #DD517F pink into #E68E36 orange at the horizon, laser magenta #FF00FF and grid blue #7998EE as neons; text #F5E9FF. Typography: chrome-gradient italic display for titles (Audiowide or Monoton; fallback: 80s italic display), Eurostile-like Michroma for labels, a clean sans for body. Shape: every hero is a horizon — a striped sun sinking into a glowing perspective floor grid; panels are dark with 1px neon outlines and 6px radius. Texture/Effects: heavy neon glow via layered magenta text-shadow, a star field above the horizon, subtle scanlines. Motion: the floor grid scrolls toward the viewer on an infinite loop; neon signs flicker once on load; 200ms hovers brighten the glow. Rules: magenta #FF00FF is exclusively for interactive glow; never light-theme this; glows are colored, never white; keep body panels nearly black so the neon can burn.',
    preview: {
      bg: '#1F1030',
      surface: '#461E52',
      text: '#F5E9FF',
      accent: '#FF00FF',
      font: 'display',
      radius: 6,
      shadow: '0 0 16px rgba(255,0,255,0.45)',
    },
  },
  {
    id: 'frutiger-aero',
    name: 'Frutiger Aero',
    category: 'retro',
    tagline: 'Glossy bubbles and blue-sky optimism.',
    prompt:
      'Palette: glossy optimism — a sky gradient #7EC8E3 to white, grass #7BB661, aqua #00A6D6, with abundant glossy white highlights; ink deep sea #1D4E68. Typography: humanist sans in the Segoe spirit — use Open Sans or Nunito Sans (fallback: rounded humanist sans), weights 400 and 600, friendly sizes. Shape: glass capsule buttons with a hard specular highlight band across the top half; rounded 18px panels of translucent white over the sky; reflective floor effects under feature imagery. Texture/Effects: bokeh circles, water droplets, lens flares, and aurora swirls drifting in the background — the world is clean, wet, and bright. Motion: bubbles float upward slowly; light sweeps cross buttons on hover; 300ms eases. Rules: every primary control needs its specular highlight — matte buttons break the illusion; backgrounds are always gradients, never flat; keep imagery natural (sky, water, grass) — no concrete or grunge; never use dark mode.',
    preview: {
      bg: '#BDE3F5',
      surface: '#FFFFFF',
      text: '#1D4E68',
      accent: '#00A6D6',
      font: 'sans',
      radius: 18,
      border: '1px solid rgba(255,255,255,0.8)',
      shadow: '0 6px 16px rgba(0,166,214,0.25)',
    },
  },
  {
    id: 'skeuo-leather',
    name: 'Leather & Linen',
    category: 'retro',
    tagline: 'A leather desk set for your data.',
    prompt:
      'Palette: saddle leather #6E4520 chrome, ruled notepaper #F3EAD3 content wells, tan #D2B48C trim, felt green #2E5E3A accents, warm ink #3B2A16. Typography: a bookish serif for content (Crimson Text; fallback: old-style serif) and engraved-effect UI labels — dark text with a 1px rgba(255,255,255,0.6) bottom highlight. Shape: the app is an object — stitched borders (dashed #C9A87C inset lines), torn-paper edges on lists, glossy multi-stop gradient buttons with 8px radius, wooden shelf rails for navigation. Texture/Effects: photoreal leather grain on the chrome, paper fiber on content, subtle page-edge stacks under cards. Motion: page-curl transitions, physical flips for state changes, 350ms with real easing weight. Rules: every material must be nameable — leather, paper, felt, brass — or it does not ship; no flat design anywhere; text sits on paper only, never directly on leather; stitching replaces plain borders throughout.',
    preview: {
      bg: '#6E4520',
      surface: '#F3EAD3',
      text: '#3B2A16',
      accent: '#2E5E3A',
      font: 'serif',
      radius: 8,
      border: '1px dashed #C9A87C',
      shadow: '0 3px 8px rgba(59,42,22,0.45)',
    },
  },
  {
    id: 'green-phosphor',
    name: 'Green Phosphor',
    category: 'retro',
    tagline: 'Phosphor glow from a patient machine.',
    prompt:
      'Palette: #0A0A0A void, phosphor green #00FF41 for all primary text and rules, dimmed green rgba(0,255,65,0.4) for secondary, amber #FFB000 strictly for the single alert or CTA per screen. Typography: monospace only — VT323 for display, IBM Plex Mono for body (fallback: terminal mono); ASCII box-drawing characters build the structure. Shape: zero radius; panels are 1px green rules or ASCII frames; the cursor is a blinking block. Texture/Effects: a horizontal scanline overlay at 6% opacity, soft phosphor bloom via text-shadow 0 0 8px rgba(0,255,65,0.6), a slight vignette at the screen edges. Motion: typewriter reveals at 20ms per character, a blinking cursor at 1s intervals, a boot sequence on first load. Rules: two colors exist — green and amber; anything else is a bug; no images, no icons — glyphs and ASCII only; never round a corner; dim the green, never grey it.',
    preview: {
      bg: '#0A0A0A',
      surface: '#0F120F',
      text: '#00FF41',
      accent: '#FFB000',
      font: 'mono',
      radius: 0,
      border: '1px solid #00FF41',
    },
  },
  {
    id: 'pixel-quest',
    name: 'Pixel Quest',
    category: 'retro',
    tagline: '8-bit adventure, 8px discipline.',
    prompt:
      'Palette: NES discipline — #000000 field, #FCFCFC white, cartridge primaries #D82800 red, #0058F8 blue, #00A800 green; four colors per component, no more. Typography: Press Start 2P for headings and Silkscreen for small labels (fallback: bitmap pixel faces); sizes locked to multiples of 8px; body text may drop to a plain mono for long passages. Shape: zero radius; 4px stepped pixel borders in #FCFCFC; hard 4px offset shadows; everything snaps to an 8px grid — no half-pixel positions ever. Texture/Effects: dithered gradients only (checkerboard fades), sprite-style icons, tiled backgrounds. Motion: stepped animation at 8-12fps with no easing — elements teleport between frames; screen-shake on errors; a blinking arrow cursor marks selection. Rules: nothing may be smooth — no anti-aliasing, no eased motion, no soft shadows; red #D82800 is for danger and CTAs only; respect the 8px grid like a religion.',
    preview: {
      bg: '#000000',
      surface: '#212134',
      text: '#FCFCFC',
      accent: '#D82800',
      font: 'display',
      radius: 0,
      border: '4px solid #FCFCFC',
    },
  },
  {
    id: 'win95',
    name: 'Windows 95',
    category: 'retro',
    tagline: 'Beige boxes, honest bevels.',
    prompt:
      'Palette: silver widget grey #C0C0C0, desktop teal #008080 page ground, navy title gradient #000080 to #1084D0, black text, white field wells. Typography: pixel-crisp 11px UI type — use Tahoma or a bitmap-style sans (fallback: MS Sans Serif lineage); no type above 15px except dialog titles. Shape: zero radius everywhere; the four-part bevel is the whole language — 2px light #FFFFFF top and left, dark #808080 and #000000 bottom and right on raised widgets, inverted for pressed states and input wells; dotted 1px focus rectangles. Texture/Effects: none — flat grey is the texture; an optional dither pattern on disabled controls. Motion: zero animation; state changes are instantaneous, as the hardware intended. Rules: never round a corner; never use a drop shadow — bevels carry all depth; title bars are always the navy gradient with white bold text; buttons must visibly depress 1px when clicked.',
    preview: {
      bg: '#008080',
      surface: '#C0C0C0',
      text: '#000000',
      accent: '#000080',
      font: 'sans',
      radius: 0,
      border: '2px outset #E0E0E0',
    },
  },
  {
    id: 'mac-platinum',
    name: 'Mac Platinum',
    category: 'retro',
    tagline: 'Pinstriped greyscale, pixel-tidy.',
    prompt:
      'Palette: platinum greys #DDDDDD and #CCCCCC, crisp #000000 1px outlines, white content wells, lavender-blue selection #6666CC as the only color. Typography: tidy bitmap spirit — a Chicago-like compact display for window titles and Geneva-like Inter at small sizes for UI (fallback: crisp compact sans); black, exact, unsmoothed in feel. Shape: windows and cards take a 1px solid #000000 outline with 2px radius; title bars carry six fine horizontal pinstripes; subtle 1px bevels inside the outline — refinement, not chunk. Texture/Effects: the pinstripe is the only ornament; selection is a flat #6666CC fill with white text; drag states show marching-ants dashed borders. Motion: nearly none — instant states, a marching-ants loop, zoom-rect transitions at most. Rules: greys stay warm-neutral — never blue-tint the platinum; #6666CC appears exclusively as selection and focus; no drop shadows beyond a hard 1px offset; never bold body text.',
    preview: {
      bg: '#DDDDDD',
      surface: '#EEEEEE',
      text: '#000000',
      accent: '#6666CC',
      font: 'sans',
      radius: 2,
      border: '1px solid #000000',
    },
  },
  {
    id: 'aqua-gel',
    name: 'Aqua Gel',
    category: 'retro',
    tagline: 'Wet blue gel you can almost press.',
    prompt:
      'Palette: pinstripe white #F4F4F4 ground, white panels, aqua gel #3B99FC for every control, brushed-metal grey #B8B8B8 for toolbars, ink #1E1E1E; traffic lights #FF5F57, #FEBC2E, #28C840 in window corners. Typography: Lucida Grande spirit — use Nunito Sans (fallback: friendly humanist sans), weights 400 and 600, slightly generous letterforms. Shape: high-gloss gel capsules — 12px radius buttons with a white specular highlight across the top third over a deeper #1D6FD1 base; panels get 10px radius and heavy window shadows 0 10px 24px rgba(0,0,0,0.35). Texture/Effects: faint vertical pinstripes on backgrounds, brushed-metal texture on chrome bars, glossy reflections under feature icons. Motion: genie-style minimizes, a bouncing dock icon for notifications, 250ms gel squish on press. Rules: every interactive element must look wet — matte buttons are forbidden; aqua blue is exclusively interactive; pinstripes stay under 4% contrast; shadows are large and soft, never hard.',
    preview: {
      bg: '#F4F4F4',
      surface: '#FFFFFF',
      text: '#1E1E1E',
      accent: '#3B99FC',
      font: 'sans',
      radius: 12,
      shadow: '0 10px 24px rgba(0,0,0,0.35)',
    },
  },
  {
    id: 'teletext',
    name: 'Teletext',
    category: 'retro',
    tagline: 'Eight colors on a broadcast grid.',
    prompt:
      'Palette: exactly eight colors, no others permitted: #000000 ground, #FF0000, #00FF00, #FFFF00, #0000FF, #FF00FF, #00FFFF, #FFFFFF; text defaults white on black. Typography: a bitmap teletext mono (VT323 or Silkscreen; fallback: coarse monospace) locked to a strict character grid; headers set double-height; page numbers in the P100 style sit top-right in yellow. Shape: everything is a character cell — full-width colored band headers (blue band, yellow text), block-mosaic graphics built from filled cells for charts and dividers, zero radius, no borders except solid color bands. Texture/Effects: none — the 8-color grid IS the texture; an optional faint CRT flicker on the whole page. Motion: page-flip refreshes that redraw top-to-bottom, blink on live values, a reveal-line for hidden answers. Rules: no color outside the eight; no type off the grid; no icons — mosaics and arrows only; charts are built from colored blocks, never vector curves.',
    preview: {
      bg: '#000000',
      surface: '#111111',
      text: '#FFFFFF',
      accent: '#FFFF00',
      font: 'mono',
      radius: 0,
      border: '2px solid #0000FF',
    },
  },
  {
    id: 'receipt-paper',
    name: 'Receipt Paper',
    category: 'retro',
    tagline: 'Your app, printed on thermal paper.',
    prompt:
      'Palette: warm counter #EDE6D3 ground, thermal paper #F6F1E5 strips, faded ink #3A3A3A (never true black — the printer is old), ledger red #C0392B for totals and alerts. Typography: Courier Prime or Special Elite (fallback: typewriter monospace) at 13-15px with an uneven, inked feel; ALL-CAPS section headers; prices right-aligned with dot leaders. Shape: content flows in narrow receipt columns (max 420px), zero radius, dashed #B9AE8F tear-lines between sections, tractor-feed hole strips down card edges, a slight 0.5deg rotation on pinned receipts. Texture/Effects: paper grain, rubber-stamp marks (PAID, VOID) at angles, barcode blocks as decorative footers. Motion: line-by-line printing reveal at 40ms per row, a subtle paper-feed shudder when new content arrives. Rules: ink is always slightly faded — #3A3A3A is maximum darkness; red appears only on money and warnings; no shadows deeper than 1px; nothing renders wider than the receipt.',
    preview: {
      bg: '#EDE6D3',
      surface: '#F6F1E5',
      text: '#3A3A3A',
      accent: '#C0392B',
      font: 'mono',
      radius: 0,
      border: '1px dashed #B9AE8F',
    },
  },
  {
    id: 'cassette-futurism',
    name: 'Cassette Futurism',
    category: 'retro',
    tagline: 'The analog cockpit of a future past.',
    prompt:
      'Palette: instrument beige #B8A88A panels on a charcoal #3E3A33 chassis, amber #FFB000 readouts, indicator red #D64533, avionics green #39FF14 for OK states; ink #2B2620 on beige. Typography: OCR-flavored — Share Tech Mono for data, Michroma for panel labels, stencil caps for section headers (fallback: technical mono plus squarish sans); label-maker tape strips for annotations. Shape: rectangular bezels with 4px radius, ribbed panel textures, chunky physical buttons with visible travel, small rounded-CRT windows for data displays; rivet dots at panel corners. Texture/Effects: scanlines inside every CRT window, plastic grain on the beige, hazard chevron tape beside destructive actions. Motion: needle gauges that step, not glide; relay-click state changes; a warm-up flicker when panels load. Rules: readouts glow only in amber, green, or red — never blue or white; every control must look pressable; no gradients except CRT glow; the machine is worn — perfection is off-brand.',
    preview: {
      bg: '#3E3A33',
      surface: '#B8A88A',
      text: '#2B2620',
      accent: '#FFB000',
      font: 'mono',
      radius: 4,
      border: '2px solid #26221C',
    },
  },
  {
    id: 'bliss-xp',
    name: 'Bliss XP',
    category: 'retro',
    tagline: 'Rolling green hills, glossy blue plastic.',
    prompt:
      'Palette: silver task chrome #ECE9D8, Luna blue gradient #245EDC to #3C8CF8 for title bars and primaries, hill green #7EC850, sunny #FFD34E, white content wells; ink #1E395B. Typography: Franklin Gothic spirit for headings (Libre Franklin; fallback: warm gothic sans), Tahoma-like small UI text; friendly, never austere. Shape: fat rounded 8px title bars with glossy vertical gradients, pill buttons with plastic shine, panels in #ECE9D8 with 1px #ACA899 borders; the close button is always orange-red. Texture/Effects: soft photographic optimism — rolling-hill greens and sky blues as section backdrops; plastic gloss highlights on every primary control. Motion: sliding task panes, balloon tooltips that pop from the corner, 250ms eases. Rules: primaries are always the Luna gradient, never flat blue; keep the palette sunny — dark mode does not exist in 2001; borders stay 1px warm grey; every window needs its glossy title bar.',
    preview: {
      bg: '#ECE9D8',
      surface: '#FFFFFF',
      text: '#1E395B',
      accent: '#245EDC',
      font: 'sans',
      radius: 8,
      border: '1px solid #ACA899',
    },
  },
  {
    id: 'web-1995',
    name: 'Web 1995',
    category: 'retro',
    tagline: 'Under construction since 1995, proudly.',
    prompt:
      'Palette: web-safe chaos — #FFFF00 page ground (or a starfield tile), #00FFFF panels, unvisited-link blue #0000EE and visited purple #551A8B, #FF00FF horizontal rules, black body text. Typography: Times New Roman defaults for body (fallback: system serif), rainbow-colored heading text, comic-style faces for welcome banners; underline every link, no exceptions. Shape: table-based layout energy — chunky 3px ridge and outset borders on everything, beveled badge buttons, zero design-system discipline, center-aligned everything. Texture/Effects: tiled background patterns, animated-GIF style starbursts and NEW! badges, a hit counter in the footer, UNDER CONSTRUCTION stripes on unfinished sections. Motion: marquee-scrolling announcements, blinking NEW! tags, a sparkle trail on the hero. Rules: links are blue and underlined or they are not links; alignment is centered by default; never use whitespace generously — pack it in; good taste is explicitly out of scope, but text stays black on light so it reads.',
    preview: {
      bg: '#FFFF00',
      surface: '#00FFFF',
      text: '#000000',
      accent: '#0000EE',
      font: 'serif',
      radius: 0,
      border: '3px ridge #808080',
    },
  },
  {
    id: 'vhs-static',
    name: 'VHS Static',
    category: 'retro',
    tagline: 'Tracking error as a design language.',
    prompt:
      'Palette: bruised tape black #1A1423, static-white text #F2F2F2, bleed red #C1121F for REC and alerts, tracking blue #3F88C5 for info bands. Typography: VCR on-screen-display energy — use Share Tech Mono (fallback: chunky terminal mono), ALL-CAPS white with a harsh 2px black shadow; timestamps in the SP 0:00:13 style as metadata labels everywhere. Shape: full-bleed frames, zero radius; UI chrome mimics camcorder overlays — PLAY, REC with a pulsing red dot, corner-anchored readouts; content panels are barely-lighter #241C31 rectangles. Texture/Effects: animated static grain at low opacity, horizontal tracking-error bands that occasionally roll through, red-blue chromatic fringe on display text. Motion: frame-drop jitter on transitions, rewind scrub effects between pages, the REC dot blinks at 1s. Rules: red #C1121F is exclusively for REC, live, and destructive actions; corners stay square — this is a CRT frame; glitches are seasoning, never applied to body text; keep the dread — no cheerful colors.',
    preview: {
      bg: '#1A1423',
      surface: '#241C31',
      text: '#F2F2F2',
      accent: '#C1121F',
      font: 'mono',
      radius: 0,
      border: '1px solid #3F88C5',
    },
  },
  {
    id: 'arcade-attract',
    name: 'Arcade Attract',
    category: 'retro',
    tagline: 'Insert coin. Press start. Glow.',
    prompt:
      'Palette: cabinet black #000000, neon #FF2079 pink, #00F0FF cyan, #FFE600 score yellow, #7CFC00 1-UP green; body text in glowing yellow or white. Typography: Press Start 2P for headers and score displays (fallback: bitmap arcade face), tabular scoreboard numerals with leading zeros (000850), a plain mono for longer text. Shape: a CRT-curved frame vignette around the viewport, marquee bands in gradient neon across section tops, zero-radius panels with 2px #00F0FF outlines and glow shadows 0 0 14px rgba(255,32,121,0.6). Texture/Effects: scanlines over everything, sprite starbursts on achievements, a high-score table as the signature list component. Motion: attract-mode loops when idle, INSERT COIN and PRESS START blinking at 800ms, screen-shake on big events, stepped sprite animation. Rules: pink #FF2079 is for CTAs and highlights only; every number is a score — zero-pad it and glow it; never soften the black; no serif faces, no lowercase headers.',
    preview: {
      bg: '#000000',
      surface: '#0D0D1A',
      text: '#FFE600',
      accent: '#FF2079',
      font: 'display',
      radius: 0,
      border: '2px solid #00F0FF',
      shadow: '0 0 14px rgba(255,32,121,0.6)',
    },
  },
  {
    id: 'swiss-grid',
    name: 'Swiss Grid',
    category: 'classic',
    tagline: 'Mathematical grid, flush-left type, one signal red.',
    prompt:
      'Palette: pure white #FFFFFF canvas, near-black ink #111111, one signal red #E30613 reserved exclusively for links, active states, and key figures — no second chromatic color anywhere. Typography: Inter or Archivo (fallback: any neutral grotesque sans); headlines huge, tight, and flush-left at weight 700; body small and precise (15px/1.5); no centered text, no italics. Shape: strict 12-column grid on an 8px base unit; 0px radius on everything; surfaces divided by hairline 1px #111111 rules rather than boxes; asymmetric layouts that leave whole columns deliberately empty. Texture/Effects: none — no shadows, no gradients, no decoration; hierarchy is size and placement alone. Motion: instant, precise state changes under 100ms. Rules: never center type; never add a second accent; borders replace shadows entirely; never round a corner; whitespace does the separating.',
    preview: {
      bg: '#FFFFFF',
      surface: '#FFFFFF',
      text: '#111111',
      accent: '#E30613',
      font: 'sans',
      radius: 0,
      border: '1px solid #111111',
    },
  },
  {
    id: 'new-wave-punk',
    name: 'New Wave Punk',
    category: 'classic',
    tagline: 'Swiss rigor, deliberately broken.',
    prompt:
      'Palette: off-white #F5F5F5 paper against dense black #0A0A0A, with one neon — magenta #FF2E88 — reserved exclusively for emphasis, hover, and violation moments; optional cyan #00E5FF only inside duotone imagery. Typography: Space Mono for body and labels, Archivo for headlines in wide-tracked uppercase; mix type sizes mid-headline and step baselines up like stairs. Shape: build a rigorous Swiss grid, then break it — one rotated block (3–8°) per screen, diagonal 2px rules slicing sections, gridlines occasionally left visible. Texture/Effects: halftone bars, registration marks, and crop ticks as ornament; no soft shadows anywhere. Motion: staccato — elements snap in stepped three-frame moves, never smooth ease. Rules: exactly one rotated element per viewport; never set body text in the neon; no gradients; no rounded corners; every violation must sit on an otherwise perfect grid.',
    preview: {
      bg: '#0A0A0A',
      surface: '#F5F5F5',
      text: '#0A0A0A',
      accent: '#FF2E88',
      font: 'mono',
      radius: 0,
    },
  },
  {
    id: 'bauhaus',
    name: 'Bauhaus',
    category: 'classic',
    tagline: 'Primary-color geometry as interface language.',
    prompt:
      'Palette: white #FFFFFF field and black #111111 ink with the three primaries — red #BE1E2D, yellow #FFDE17, blue #21409A — used as flat geometric planes; red is the sole interactive accent. Typography: Jost or Josefin Sans (fallback: geometric sans) with lowercase-heavy headlines — capitals are optional, geometry is not; DM Sans for body. Shape: circles, triangles, and squares as literal UI motifs (avatars circular, alerts triangular, cards square); 0px radius; thick 8px black divider bars; modular grid with primary-colored blocks anchoring each section. Texture/Effects: flat color only — no gradients, no shadows, no outlines thinner than 2px. Motion: geometric — elements rotate or slide along one axis, 200ms, near-linear easing. Rules: never tint or shade the primaries; yellow never carries text; one geometric motif per component, not three; no drop shadows ever.',
    preview: {
      bg: '#FFFFFF',
      surface: '#FFDE17',
      text: '#111111',
      accent: '#BE1E2D',
      font: 'sans',
      radius: 0,
      border: '3px solid #111111',
    },
  },
  {
    id: 'de-stijl',
    name: 'De Stijl',
    category: 'classic',
    tagline: 'Black gridlines, primary rectangles, layout as art.',
    prompt:
      'Palette: white #FFFFFF canvas partitioned by black #111111 strokes into cells of red #DD0100, yellow #FAC901, and blue #225095; most cells stay white — color is scarce and asymmetric. Typography: Archivo for all-caps labels and headlines, Inter Tight for body; type sits inside cells, never across borders. Shape: the layout IS the artwork — asymmetric rectangular cells divided by heavy 4–8px solid black borders that run edge to edge; 0px radius; no symmetry between cells. Texture/Effects: perfectly flat fills; no gradients, no shadows, no imagery bleeding across gridlines. Motion: cells slide open along their black borders, 200ms, no bounce. Rules: black lines must always reach another line or the viewport edge; never show more than three colored cells per screen; never round a corner; red is the only button color.',
    preview: {
      bg: '#FFFFFF',
      surface: '#FFFFFF',
      text: '#111111',
      accent: '#DD0100',
      font: 'sans',
      radius: 0,
      border: '6px solid #111111',
    },
  },
  {
    id: 'constructivist',
    name: 'Constructivist',
    category: 'classic',
    tagline: 'Diagonal propaganda energy in red, black, and cream.',
    prompt:
      'Palette: aged cream #F2E8DC ground, black #1A1A1A ink, and revolutionary red #C8102E — red owns every call to action, wedge, and diagonal bar; no other color is permitted. Typography: Oswald or Archivo Black (fallback: condensed grotesque) in shouting uppercase; key headlines angled 10–15°; body short, bold, and flush against hard edges. Shape: diagonal composition — red wedges and black bars crossing the layout, sections broken by angled dividers rather than horizontals; 0px radius; asymmetric weight balanced like a poster. Texture/Effects: grainy duotone photography (black plus red) only; flat fills elsewhere; no soft shadows. Motion: bold diagonal wipes and reveals, 250ms, decisive. Rules: at least one diagonal per screen; never angle body text; never soften red with tints; no gradients; photography is duotone or absent.',
    preview: {
      bg: '#F2E8DC',
      surface: '#F2E8DC',
      text: '#1A1A1A',
      accent: '#C8102E',
      font: 'display',
      radius: 0,
      border: '2px solid #1A1A1A',
    },
  },
  {
    id: 'dada-collage',
    name: 'Dada Collage',
    category: 'classic',
    tagline: 'Anti-design collage of clashing type and pasted scraps.',
    prompt:
      'Palette: xerox black #141414 on scrap-paper white #F4F1EA, with red #D0342C reserved exclusively for rubber-stamp marks and interactive elements. Typography: deliberate clash — Special Elite typewriter for body, Anton fragments and EB Garamond words colliding inside a single headline; sizes intentionally mismatched (fallback: monospace plus any two clashing display faces). Shape: overlapping rotated scraps (±2–5°) with hard cut edges; every surface looks pasted on, layered with small offset paper shadows (2–3px, barely blurred); 0px radius on the cuts. Texture/Effects: newsprint scan grain, torn-edge masks on images, stamped labels set at slight angles. Motion: jittery — elements drop in and settle with one small corrective twitch. Rules: at least two clashing fonts per headline, never one; nothing aligns cleanly — everything slightly off; red only as stamp or action; never use smooth gradients or gloss.',
    preview: {
      bg: '#F4F1EA',
      surface: '#FFFFFF',
      text: '#141414',
      accent: '#D0342C',
      font: 'mono',
      radius: 0,
      shadow: '3px 3px 0 rgba(20,20,20,0.2)',
    },
  },
  {
    id: 'art-deco',
    name: 'Art Deco',
    category: 'classic',
    tagline: 'Gold on black, sunbursts, stepped symmetry.',
    prompt:
      'Palette: deep black #0D0D0D stage, gold #D4AF37 ornament (support tone #C9A227), cream #F5EFE0 type, optional jade #1F5E4E panels — gold is exclusively for frames, rules, and the primary action. Typography: Poiret One or Cinzel (fallback: geometric display serif) for letterspaced uppercase headlines; Josefin Sans body; generous tracking everywhere. Shape: strict bilateral symmetry; stepped and chevron borders; sunburst and fan motifs radiating from headers; thin double-line 1px gold frames around every card; 0px radius — corners are stepped, not rounded. Texture/Effects: a subtle metallic gradient permitted only inside gold elements; everything else flat. Motion: slow, dignified fades with an occasional gold shimmer sweep on hover. Rules: never break symmetry; gold text only on black, never on cream; no bright chromatic accents; no drop shadows — frames carry the depth.',
    preview: {
      bg: '#0D0D0D',
      surface: '#0D0D0D',
      text: '#F5EFE0',
      accent: '#D4AF37',
      font: 'display',
      radius: 0,
      border: '3px double #D4AF37',
    },
  },
  {
    id: 'art-nouveau',
    name: 'Art Nouveau',
    category: 'classic',
    tagline: 'Whiplash curves and botanical ornament on warm cream.',
    prompt:
      'Palette: cream #F3ECDD ground with lighter paper #FAF5E8 panels, olive #708238 for interactive elements exclusively, ochre gold #C89B3C ornament, dusty rose #C98A7D highlights, warm ink #433A2E text. Typography: Cormorant Garamond (fallback: old-style serif) at generous sizes with true italics and an ornamental drop cap opening each major section; small caps for labels. Shape: arched tops on cards and imagery (24px+ radius on upper corners only), curved vine-like dividers, borders that swell and taper like plant stems; nothing perfectly straight where a curve will do. Texture/Effects: fine engraved linework, botanical corner flourishes, paper warmth — no plastic gloss. Motion: organic — dividers and vines draw themselves in on scroll, 400–600ms ease-out. Rules: never use hard 90° frames; olive is the only button color; no gray anywhere — every neutral is warm; ornament frames content, never replaces it.',
    preview: {
      bg: '#F3ECDD',
      surface: '#FAF5E8',
      text: '#433A2E',
      accent: '#708238',
      font: 'serif',
      radius: 18,
      border: '1px solid #C89B3C',
    },
  },
  {
    id: 'memphis',
    name: 'Memphis',
    category: 'classic',
    tagline: 'Squiggles, terrazzo, and pastel-neon clash.',
    prompt:
      'Palette: warm white #FDF6F0 ground with clashing pink #FF4FA3, teal #00C2D1, and yellow #FFD400 — pink owns primary actions, teal owns the offset shadows, black #111111 outlines everything. Typography: Fredoka (fallback: rounded display sans) for chunky headlines, Space Grotesk for body. Shape: deliberately mixed radii — one card 0px, the next 16px, a circle beside them; thick 2–3px black outlines on every element; geometric confetti (triangles, squiggles, half-circles) scattered as decoration. Texture/Effects: squiggle and zigzag pattern fills, terrazzo chips across empty areas, color-offset drop shadows (4px 4px 0 in a clashing hue — never black, never blurred). Motion: bouncy overshoot on entrances and hovers, 250–350ms spring. Rules: never match radii across siblings; shadows must be colored and hard; no gradients; keep at least three shape motifs on every screen.',
    preview: {
      bg: '#FDF6F0',
      surface: '#FFFFFF',
      text: '#111111',
      accent: '#FF4FA3',
      font: 'display',
      radius: 12,
      border: '2px solid #111111',
      shadow: '4px 4px 0 #00C2D1',
    },
  },
  {
    id: 'mid-century',
    name: 'Mid-Century',
    category: 'classic',
    tagline: 'Mustard, teal, and walnut print modernism.',
    prompt:
      'Palette: cream #F6EFE3 paper, walnut #4A342A ink, with mustard #E3A72F, teal #137A7F, and burnt orange #C65D2E as flat spot colors — teal is the interactive accent, orange the highlight, mustard the fill. Typography: Josefin Sans (fallback: geometric mid-mod sans) for headlines, Lora for body; sentence case, relaxed leading. Shape: 8–12px card radius; atomic ornament — small dots joined by thin 1px lines, kidney and boomerang blobs behind hero content; friendly asymmetric layouts. Texture/Effects: soft paper grain, a slight screenprint offset where colors meet; no gloss, no black — walnut is the darkest tone. Motion: gentle retro slides and fades, 250ms ease. Rules: never use pure black or pure white; keep every color flat, no gradients; one blob per section maximum; shadows barely-there (0 1px 2px) or absent.',
    preview: {
      bg: '#F6EFE3',
      surface: '#FFFFFF',
      text: '#4A342A',
      accent: '#137A7F',
      font: 'sans',
      radius: 10,
    },
  },
  {
    id: 'atomic-googie',
    name: 'Atomic Googie',
    category: 'classic',
    tagline: 'Space-race optimism: starbursts and diner neon.',
    prompt:
      'Palette: mint wash #CDEBD8 ground, deep slate #23383B ink, aqua #40E0D0 chrome trim, cherry red #D72638 exclusively for primary actions, mustard #E9B44C badges. Typography: Righteous (fallback: retro display sans) for headlines, Pacifico for one script flourish per page, Barlow for body. Shape: pill buttons and big 20–24px rounded cards; upswept diagonal section edges; starburst icons punctuating stats and highlights; chrome-strip dividers drawn as thin double lines. Texture/Effects: soft neon glow shadows on interactive elements (0 0 12px in the element color — never plain black), light halftone on imagery. Motion: zippy rocket slides with a slight arc, a quick twinkle on success states, 200–300ms. Rules: shadows glow in color or do not exist; one script accent per page, never body script; no sharp corners on anything clickable; diagonals sweep upward, never droop.',
    preview: {
      bg: '#CDEBD8',
      surface: '#FFFFFF',
      text: '#23383B',
      accent: '#D72638',
      font: 'display',
      radius: 22,
      shadow: '0 0 12px rgba(64,224,208,0.6)',
    },
  },
  {
    id: 'pop-art',
    name: 'Pop Art',
    category: 'classic',
    tagline: 'Ben-Day dots, thick outlines, comic-panel punch.',
    prompt:
      'Palette: yellow #F7D038 punch panels on white #FFFFFF, red #E5352B for actions and POW moments, blue #2A5CAA fills, black #111111 outlines on everything. Typography: Bangers (fallback: comic display) for headlines and callouts, Inter for body; headlines large, tilted a degree or two, often inside speech bubbles. Shape: comic-panel layout — content in bordered 3px black boxes arranged like a page of panels; 0px radius; starburst badges for numbers and alerts; speech-bubble tooltips with tails. Texture/Effects: Ben-Day halftone dot fills in panel backgrounds and image treatments; flat CMYK-style color, no gradients. Motion: snap zooms and panel flips, 150ms, comic-fast. Rules: every surface gets a black border, no exceptions; dots are the only texture; never use soft shadows; red means action and nothing else.',
    preview: {
      bg: '#F7D038',
      surface: '#FFFFFF',
      text: '#111111',
      accent: '#E5352B',
      font: 'display',
      radius: 0,
      border: '3px solid #111111',
    },
  },
  {
    id: 'psychedelia',
    name: 'Psychedelia',
    category: 'classic',
    tagline: 'Melting type and vibrating complementary color.',
    prompt:
      'Palette: deep violet #2B0245 ground melting into purple #6A00F4 surfaces, with orange #FF5E00, magenta #FF00A0, and acid green #9EF01A vibrating against each other — acid green is reserved exclusively for interactive elements. Typography: Shrikhand (fallback: swollen display serif) for melting headlines, Space Grotesk for body set light (#FDF0FF) on the dark ground. Shape: no straight edges — flowing blob containers, wavy section dividers, concentric contour lines radiating behind heroes; radius large and irregular (24px+). Texture/Effects: slow swirl gradients between the palette hues, subtle moiré interference in backgrounds. Motion: liquid — elements warp and drift on 800ms+ eases, background hues shifting a few degrees continuously. Rules: complementary colors must touch to vibrate; never use a hard horizontal divider; no grays anywhere; keep body text on the darkest ground for sanity; one melting headline per screen.',
    preview: {
      bg: '#2B0245',
      surface: '#6A00F4',
      text: '#FDF0FF',
      accent: '#9EF01A',
      font: 'display',
      radius: 24,
    },
  },
  {
    id: 'punk-zine',
    name: 'Punk Zine',
    category: 'classic',
    tagline: 'Xerox-and-glue rebellion with ransom-note type.',
    prompt:
      'Palette: photocopy black #111111 and white #F5F2EA only, plus raw red #E10600 reserved for exactly one purpose — the action that matters (alt: acid yellow #F7E017 highlighter strips). Typography: ransom-note mix — Anton fragments, Special Elite typewriter runs, and Permanent Marker scrawl colliding in headlines; body stays typewriter for legibility (fallback: heavy display plus monospace). Shape: torn-edge containers, elements taped or stapled on (visible tape strips as decoration), scraps rotated ±3°; 0px radius — nothing in a zine is rounded. Texture/Effects: photocopy grain over everything, toner streaks, dithered high-contrast imagery, hard 2px offset shadows suggesting pasted paper. Motion: abrupt cuts and jitters, no easing curves — things appear, they do not glide. Rules: never smooth an edge; maximum one red element per viewport; no gradients, no soft shadows, no pastels; if it looks designed, wreck it slightly.',
    preview: {
      bg: '#F5F2EA',
      surface: '#F5F2EA',
      text: '#111111',
      accent: '#E10600',
      font: 'display',
      radius: 0,
      border: '2px solid #111111',
      shadow: '2px 2px 0 #111111',
    },
  },
  {
    id: 'raw-html',
    name: 'Raw HTML',
    category: 'classic',
    tagline: 'Browser defaults, worn proudly.',
    prompt:
      'Palette: browser defaults, worn proudly — white #FFFFFF page, black #000000 text, link blue #0000EE (visited purple #551A8B), fieldset gray #808080 rules. Typography: Tinos or PT Serif standing in for Times New Roman (fallback: the system serif stack — the default IS the point); 16px body, headings only via the native h1–h6 scale, bold and underline the only emphasis. Shape: a full-width flowing document; no cards, no columns, no max-width vanity — content simply stacks; 1px #808080 horizontal rules divide sections; 0px radius; default-looking bordered buttons and inputs. Texture/Effects: none whatsoever — no shadows, no gradients, no background images, no icons beyond text glyphs. Motion: none; navigation feels like a page load, not a transition. Rules: links stay blue and underlined, always; never style a table beyond its borders; no custom spacing system — let the user-agent stylesheet breathe; decoration is forbidden.',
    preview: {
      bg: '#FFFFFF',
      surface: '#FFFFFF',
      text: '#000000',
      accent: '#0000EE',
      font: 'serif',
      radius: 0,
      border: '1px solid #808080',
    },
  },
  {
    id: 'wabi-sabi',
    name: 'Wabi-Sabi',
    category: 'classic',
    tagline: 'Earthy quiet, honest imperfection, breathing room.',
    prompt:
      'Palette: oatmeal #EDE6DA ground, warm paper surfaces #F4EFE5, charcoal #3B3733 ink, clay #B08968 reserved for interactive elements, sage #8A8F7C for quiet secondary marks. Typography: Shippori Mincho (fallback: humanist serif) for headlines at modest sizes, Karla for body; nothing bold — weights 400–500 only, hierarchy through space. Shape: slightly irregular — cards with softly uneven 4–6px radius, hand-drawn brush-stroke dividers instead of rules, asymmetric margins that never quite match. Texture/Effects: washi paper grain, an occasional ink-blot mark, one thin gold kintsugi hairline (1px #C89B3C) accepted as a visible seam, not hidden. Motion: breath-like — 600ms fades, nothing faster; elements settle rather than arrive. Rules: never perfect the alignment — small asymmetries are the brand; no pure white or pure black; no shadows, only paper; never animate more than one element at a time.',
    preview: {
      bg: '#EDE6DA',
      surface: '#F4EFE5',
      text: '#3B3733',
      accent: '#B08968',
      font: 'serif',
      radius: 5,
    },
  },
  {
    id: 'ukiyo-e',
    name: 'Ukiyo-e',
    category: 'classic',
    tagline: 'Flat woodblock planes in Prussian blue and vermilion.',
    prompt:
      'Palette: washi cream #F2E8DC paper, sumi ink #2B2B28, Prussian blue #003153 for structural planes and frames, vermilion #E34234 reserved exclusively for seals, actions, and the single red accent per view. Typography: Noto Serif JP (fallback: high-contrast serif) for display, Noto Sans JP or Karla for body; occasional short vertical text labels as ornament. Shape: flat layered planes stacked like a woodblock print — no perspective, depth from overlap alone; scalloped wave and cloud dividers between sections; hanko-style red square badges for status marks; 0px radius, 2px Prussian blue keylines. Texture/Effects: faint woodgrain in large fills, bokashi gradient bands (a single hue fading into paper) at section tops. Motion: slow parallax between the flat planes, waves drifting sideways. Rules: colors stay flat inside their keylines; vermilion never exceeds five percent of a screen; no drop shadows — overlap implies depth; never use glossy or neon color.',
    preview: {
      bg: '#F2E8DC',
      surface: '#F2E8DC',
      text: '#2B2B28',
      accent: '#E34234',
      font: 'serif',
      radius: 0,
      border: '2px solid #003153',
    },
  },
  {
    id: 'broadsheet',
    name: 'Broadsheet',
    category: 'editorial',
    tagline: 'Front-page authority in dense inked columns.',
    prompt:
      'Palette: newsprint #F8F5EE, ink #1A1A1A, and a restrained dark red #8B0000 used exclusively for kickers, links, and breaking labels — never for decoration. Typography: a blackletter masthead in UnifrakturCologne (fallback: blackletter display), headlines in Playfair Display, body in Source Serif 4 at 16–17px, justified where columns allow; bylines and folios in small caps. Shape: a dense multi-column grid (3–5 columns) separated by 1px vertical rules; hairline horizontal rules between stories; 0px radius; no cards — stories are divided, not boxed. Texture/Effects: none; the density is the texture. Motion: nearly none — instant loads, no scroll theatrics. Rules: never orphan a headline from its rule; red appears in text only, never as a fill; no shadows, no gradients, no rounded anything; every image gets a caption and a credit line.',
    preview: {
      bg: '#F8F5EE',
      surface: '#F8F5EE',
      text: '#1A1A1A',
      accent: '#8B0000',
      font: 'serif',
      radius: 0,
      border: '1px solid #1A1A1A',
    },
  },
  {
    id: 'tabloid',
    name: 'Tabloid',
    category: 'editorial',
    tagline: 'Red-top shout with headlines at viewport scale.',
    prompt:
      'Palette: white #FFFFFF ground, black #111111 slabs, masthead red #D0021B owning the top bar and every action, highlight yellow #FFE100 strips under key phrases. Typography: Anton (fallback: ultra-condensed display) headlines at absurd scale — 10–15vw, uppercase, tight leading; Roboto Condensed body packed underneath. Shape: heavy black boxes and bars framing content; yellow highlighter strips behind standout stats; cut-out-style imagery overlapping its container edges; 0px radius. Texture/Effects: flat and loud — no gradients, hard edges everywhere; the occasional white headline knocked out of a black or red slab. Motion: snap transitions, 100ms, no easing softness. Rules: the headline is the layout — everything else fits around it; never shrink the headline to fit, cut words instead; red and yellow never touch; no soft shadows; sentence case is banned in headlines.',
    preview: {
      bg: '#FFFFFF',
      surface: '#FFE100',
      text: '#111111',
      accent: '#D0021B',
      font: 'display',
      radius: 0,
      border: '4px solid #111111',
    },
  },
  {
    id: 'vogue-mono',
    name: 'Vogue Mono',
    category: 'editorial',
    tagline: 'Hairline didone luxury in black and white.',
    prompt:
      'Palette: black #0A0A0A on white #FFFFFF, full stop; lipstick red #C1121F permitted only once per view — a single link, a single word, a sale price. Typography: Bodoni Moda (fallback: high-contrast didone serif) for enormous hairline headlines — light weights only; Tenor Sans in wide-tracked uppercase for labels and body. Shape: vast whitespace, full-bleed photography, centered lockups over images; hairline 1px #0A0A0A rules as the only structure; 0px radius; no boxes, no cards. Texture/Effects: monochrome imagery preferred; no shadows, no gradients, no color casts. Motion: slow crossfades and a subtle Ken Burns drift on imagery, 800ms and longer. Rules: never bold the didone; never use two accents; typography must be able to carry a page with no image at all; when in doubt, remove the element.',
    preview: {
      bg: '#FFFFFF',
      surface: '#FFFFFF',
      text: '#0A0A0A',
      accent: '#C1121F',
      font: 'serif',
      radius: 0,
    },
  },
  {
    id: 'kinfolk-air',
    name: 'Kinfolk Air',
    category: 'editorial',
    tagline: 'Slow-living oat tones and breathable grids.',
    prompt:
      'Palette: oat #FAF6F0 ground, white #FFFFFF surfaces, soft black #33302C ink, warm gray #8C8680 captions, terracotta #C4886F reserved exclusively for links and the rare button. Typography: Cormorant Garamond Light (fallback: delicate old-style serif) for large airy headlines — never bold; Karla small (13–14px) for body and wide-tracked uppercase labels. Shape: a sparse 12-column grid where content occupies half and air the rest; images unboxed with wide uneven margins; thin 1px #E5DFD6 rules; 0px radius — softness comes from color, not corners. Texture/Effects: nothing beyond warm paper tones; photography muted and natural. Motion: soft 400ms fades on scroll, one element at a time. Rules: whitespace is the layout — never fill it; no pure white-on-white contrast tricks and no pure black; headlines stay light-weight always; terracotta never becomes a background fill; no shadows.',
    preview: {
      bg: '#FAF6F0',
      surface: '#FFFFFF',
      text: '#33302C',
      accent: '#C4886F',
      font: 'serif',
      radius: 0,
    },
  },
  {
    id: 'briefing-desk',
    name: 'Briefing Desk',
    category: 'editorial',
    tagline: 'Numbered columns, global-affairs polish.',
    prompt:
      'Palette: off-white #FBFAF7, ink #14181C, brisk red #D0342C for numbered kickers and live markers exclusively, navy #24364D for secondary structure. Typography: Source Serif 4 body at 16px in disciplined columns; Inter or Archivo (fallback: neutral grotesque) for numbered section kickers — 01, 02, 03 — in bold uppercase with wide tracking. Shape: an intricate multi-column grid, every module opening with its number and a thin keyline; small rectangular category badges; 0–2px radius; content dense but ruthlessly ordered. Texture/Effects: none — precision is the aesthetic; hairline #D8D4CC keylines carry all separation. Motion: crisp and minimal — 150ms fades, no movement for its own sake. Rules: every section is numbered, no exceptions; red never fills a surface, only marks it; no shadows; density must never cost alignment — if it cannot line up, cut it.',
    preview: {
      bg: '#FBFAF7',
      surface: '#FFFFFF',
      text: '#14181C',
      accent: '#D0342C',
      font: 'sans',
      radius: 0,
      border: '1px solid #14181C',
    },
  },
  {
    id: 'literary-journal',
    name: 'Literary Journal',
    category: 'editorial',
    tagline: 'One classical column, drop caps, oxblood ink.',
    prompt:
      'Palette: cream #FBF8F1, near-black ink #22211E, oxblood #7C3030 reserved exclusively for links, drop caps, and small-caps headings. Typography: EB Garamond or Spectral (fallback: bookish old-style serif) at 19–21px on a 65ch measure with 1.7 line-height; italics for pull-quotes, true small caps for section heads, an ornamental drop cap opening each piece. Shape: a single centered column and nothing else — no sidebars, no cards, no boxes; sections divided by asterisms (⁂) or a short centered rule; 0px radius. Texture/Effects: none; the page is paper and ink. Motion: none — the reader scrolls, the page holds still. Rules: never exceed the measure; no sans-serif anywhere; no color beyond oxblood; no shadows, borders, or backgrounds behind text; typography and spacing are the entire design.',
    preview: {
      bg: '#FBF8F1',
      surface: '#FBF8F1',
      text: '#22211E',
      accent: '#7C3030',
      font: 'serif',
      radius: 0,
    },
  },
  {
    id: 'latex-paper',
    name: 'LaTeX Paper',
    category: 'editorial',
    tagline: 'Numbered sections, footnotes, scholarly rigor.',
    prompt:
      'Palette: white #FFFFFF, black #000000 text, link blue #0F4C81 for cross-references and hyperlinks exclusively, citation green #2E7D32 for reference markers. Typography: STIX Two Text (fallback: Computer Modern-flavored serif) justified with hyphenation; IBM Plex Mono for code and identifiers; numbered headings (1, 1.1, 1.2) in bold, never decorative. Shape: a single column at a scholarly measure; the abstract set inside a 1px black rule-box; footnotes at true page bottom in smaller type; tables with top and bottom rules only — never vertical lines; figures numbered and captioned. Texture/Effects: none — this is a paper, not a website. Motion: none. Rules: every heading, figure, and table is numbered; vertical table rules are forbidden; no color beyond the two functional hues; no shadows, radius, or decoration of any kind; justify the body, always.',
    preview: {
      bg: '#FFFFFF',
      surface: '#FFFFFF',
      text: '#000000',
      accent: '#0F4C81',
      font: 'serif',
      radius: 0,
      border: '1px solid #000000',
    },
  },
  {
    id: 'risograph',
    name: 'Risograph',
    category: 'editorial',
    tagline: 'Two fluorescent inks, deliberately misaligned.',
    prompt:
      'Palette: pick exactly two inks per page from fluoro pink #FF48B0, federal blue #0078BF, sunflower #FFE800, green #00A95C, and orange #FF6C2F, printed on cream #F7F2E7 — the default pair is blue ink with pink accents, and the two inks are the entire palette, blue doubling as text color. Typography: Archivo Black or Syne (fallback: chunky display sans) for headlines, Space Grotesk for body, all set in ink color, never black. Shape: chunky simple shapes with a deliberate 1–3px misregistration offset between layers; slight 4px radius; duotone images blended multiply. Texture/Effects: heavy grain over every fill, uneven roller coverage on large areas, overprint zones where the two inks mix into a third color. Motion: layers slide subtly out of and back into alignment on hover. Rules: two inks maximum, ever; no true black anywhere; misalign on purpose, not everywhere; no gradients except grain.',
    preview: {
      bg: '#F7F2E7',
      surface: '#F7F2E7',
      text: '#0078BF',
      accent: '#FF48B0',
      font: 'display',
      radius: 4,
      shadow: '3px 3px 0 #FF48B0',
    },
  },
  {
    id: 'letterpress',
    name: 'Letterpress',
    category: 'editorial',
    tagline: 'Deep-pressed type on cotton paper.',
    prompt:
      'Palette: cotton cream #F4EBD9, soft ink #2F2A25 (never true black — ink sinks into paper), vermilion #C73E1D reserved for ornaments and the primary action, duck-egg #B7C9C4 for quiet secondary panels. Typography: Playfair Display or IM Fell English (fallback: printed-book serif) for centered lockups, Rye for occasional wood-type headers, generous letterspacing on caps. Shape: centered symmetric compositions; thin-thick double borders (3px double #2F2A25) framing cards; ornament rules (❦) between sections; tiny 2px radius softening the plate edges. Texture/Effects: the deboss is the signature — a pressed inset look on display type via subtle inner highlight (0 1px 1px rgba(255,255,255,0.6)), paper-tooth grain, slight ink squash on heavy letters. Motion: press-stamp entrances — elements arrive with a firm 150ms settle. Rules: never use flat pure black; center the lockups, always; no glossy shadows — only the pressed inset; ornaments punctuate, never wallpaper.',
    preview: {
      bg: '#F4EBD9',
      surface: '#F4EBD9',
      text: '#2F2A25',
      accent: '#C73E1D',
      font: 'serif',
      radius: 2,
      border: '3px double #2F2A25',
    },
  },
  {
    id: 'blueprint',
    name: 'Blueprint',
    category: 'editorial',
    tagline: 'White linework drafted on Prussian blue.',
    prompt:
      'Palette: blueprint blue field #123B6D with panels #1A3E7A, line white #DCE7F5 for every stroke and label, pale cyan #7FB3D5 reserved for interactive elements and highlights. Typography: IBM Plex Mono for dimensions, data, labels, and body — this is a drawing, not a document; Architects Daughter (fallback: casual handwriting) for hand-note annotations in the margins. Shape: everything drawn in 1px white linework — outlined, never filled; dashed lines mark construction and pending states; a corner title block (project, sheet, date) anchors each page; faint grid-paper lines across the field; 0px radius. Texture/Effects: a slight cyanotype unevenness in the blue field; no other texture. Motion: lines draw themselves in, 400–600ms, like a pen plotting. Rules: white lines on blue, never the inverse; no fills except the paper itself; no shadows or gradients; every diagram gets a dimension or a label; nothing rounded.',
    preview: {
      bg: '#123B6D',
      surface: '#1A3E7A',
      text: '#DCE7F5',
      accent: '#7FB3D5',
      font: 'mono',
      radius: 0,
      border: '1px solid #DCE7F5',
    },
  },
  {
    id: 'spec-sheet',
    name: 'Spec Sheet',
    category: 'editorial',
    tagline: 'Hairline tables, mono labels, safety orange.',
    prompt:
      'Palette: white #FFFFFF, ink #111417, safety orange #E85D04 reserved exclusively for callouts, active states, and out-of-tolerance values. Typography: IBM Plex Mono for all data, labels, and part numbers with tabular numerals mandatory on every figure; Inter Tight (fallback: tight grotesque) for headings; body compact at 14px. Shape: hairline 1px tables as the primary layout device; crosshair registration marks at section corners; dimension-arrow annotations pointing at key numbers; part-number codes (REV 2.1, PN-0047) as decoration that carries meaning; 0px radius. Texture/Effects: none — a datasheet has no mood, only information; separation by hairlines and whitespace. Motion: values tick upward numerically on load; nothing else moves. Rules: numbers align in columns, always tabular; orange marks meaning, never mood; no shadows, no gradients, no rounded corners; every figure carries its unit; if it is not measurable, it does not get orange.',
    preview: {
      bg: '#FFFFFF',
      surface: '#FFFFFF',
      text: '#111417',
      accent: '#E85D04',
      font: 'mono',
      radius: 0,
      border: '1px solid #111417',
    },
  },
  {
    id: 'field-notes',
    name: 'Field Notes',
    category: 'editorial',
    tagline: 'Kraft covers, graph paper, stamped in red.',
    prompt:
      'Palette: kraft #C4A265 covers and chrome, warm paper #F7F3E8 pages, pencil gray #5B5850 ink, graph-line green #CBD5C0 ruling, stamp red #B33A3A reserved for stamps and primary actions. Typography: Inter for UI chrome, Courier Prime for logged entries and data, Caveat for margin annotations (fallback: honest workhorse sans plus a handwriting accent). Shape: booklet-rounded 10px corners; stitched dashed borders (1px dashed #5B5850) on cards; ruled and graph-paper backgrounds inside content wells; taped corners and a kraft header band. Texture/Effects: paper grain, the occasional coffee-ring or pencil smudge as easter-egg decoration, rubber-stamp date marks set at an angle. Motion: page-turn transitions between views, sketch-style draw-in for charts. Rules: handwriting is for annotation only, never headings or body; keep the ruling faint — content over paper; red appears stamped and slightly rotated, never as a flat fill; no glossy shadows.',
    preview: {
      bg: '#C4A265',
      surface: '#F7F3E8',
      text: '#5B5850',
      accent: '#B33A3A',
      font: 'mono',
      radius: 10,
      border: '1px dashed #5B5850',
    },
  },
  {
    id: 'index-card',
    name: 'Index Card',
    category: 'editorial',
    tagline: 'Ruled manila cards pinned to cork.',
    prompt:
      'Palette: cork #B98B5E board behind manila #F1E4C0 cards, ink #3E3A33, ruled blue #A8C0D8 lines across card bodies, header red #E07A5F for the top rule and actions. Typography: Kalam (fallback: neat handwriting) for card titles as if written on the top line, IBM Plex Mono for metadata and stamps, Inter for surrounding UI. Shape: every content unit is a ruled index card — red top line, blue rules below, 6px corners, rotated ±1.5° at random; cards pinned or taped to the board; stacks reveal 2–3px of the card beneath. Texture/Effects: soft small paper shadows (0 2px 4px rgba(62,58,51,0.3)), cork grain on the background, pushpin dots. Motion: drag with paper physics — cards lift, tilt toward the cursor, and flick-settle on drop. Rules: no card sits perfectly straight; ruling stays inside cards, never on the board; shadows stay soft and small — nothing floats high; handwriting never sets body paragraphs.',
    preview: {
      bg: '#B98B5E',
      surface: '#F1E4C0',
      text: '#3E3A33',
      accent: '#E07A5F',
      font: 'display',
      radius: 6,
      shadow: '0 2px 4px rgba(62,58,51,0.3)',
    },
  },
  {
    id: 'typewriter-draft',
    name: 'Typewriter Draft',
    category: 'editorial',
    tagline: 'Monospace manuscript with red corrections.',
    prompt:
      'Palette: draft paper #FAF8F2, ribbon ink #333333 (a worn soft black, never #000000), correction red #C0392B reserved for edits, deletions, and the primary action. Typography: Courier Prime for everything (fallback: any true monospace); Special Elite for headers wanting a struck-key roughness; underline is the only emphasis — no bold, no italic. Shape: a single column of roughly 80 characters, double-spaced (line-height 2); strikethrough corrections left visible beside their red replacements; DRAFT stamped diagonally in red outline behind hero sections; 0px radius, no boxes — margins are the structure. Texture/Effects: faint paper fiber, slightly uneven ink weight per character, the occasional double-struck letter. Motion: hero copy types itself on; view changes jump like a carriage return — instant, with a soft mechanical settle. Rules: one typeface family per page; never justify — ragged right always; red means correction, nothing else; no shadows, cards, or gradients.',
    preview: {
      bg: '#FAF8F2',
      surface: '#FAF8F2',
      text: '#333333',
      accent: '#C0392B',
      font: 'mono',
      radius: 0,
    },
  },
  {
    id: 'gallery-catalog',
    name: 'Gallery Catalog',
    category: 'editorial',
    tagline: 'Vast white walls, whispering captions.',
    prompt:
      'Palette: pure white #FFFFFF walls, black #0A0A0A for titles, caption gray #9A9A9A for everything secondary — gray IS the accent; there is no chromatic color. Typography: Inter small and quiet (13–14px) for captions and body; artwork and section titles in Spectral italic (fallback: literary serif); plate numbers (Fig. 04) in wide-tracked uppercase. Shape: massive margins — content occupies the center third; images unframed and unshadowed on the white field; a single hairline 1px #E8E8E8 rule above the footer as the only line on the page; 0px radius. Texture/Effects: none — the work is the color, the wall stays silent. Motion: slow deliberate 600ms fades, one artwork at a time; no parallax, no zoom. Rules: never frame or shadow an image; captions stay gray and small — they whisper; no chromatic accent ever; empty space is the exhibition, do not fill it.',
    preview: {
      bg: '#FFFFFF',
      surface: '#FFFFFF',
      text: '#0A0A0A',
      accent: '#9A9A9A',
      font: 'sans',
      radius: 0,
    },
  },
  {
    id: 'pulp-cover',
    name: 'Pulp Cover',
    category: 'editorial',
    tagline: 'Dime-novel drama in lurid yellow and red.',
    prompt:
      'Palette: pulp yellow #F2C744 cover ground, deep teal #12494D masthead and ink, blood red #C1272D for banners, prices, and every action, aged page #E8D8A8 for interior surfaces. Typography: Anton or Bowlby One (fallback: heavy display) for lurid stacked titles, Barlow Condensed for body; taglines set in breathless italic caps ("SHE KNEW TOO MUCH"). Shape: diagonal red banners cutting across corners, rough-edged frames around imagery, starburst price badges (10¢), a 3px teal border boxing the cover composition; 0px radius. Texture/Effects: halftone painted-illustration treatment on imagery, worn edges and slight foxing on surfaces, flat saturated fills elsewhere. Motion: dramatic 300ms zoom entrances, cover-flip transitions between sections. Rules: every hero needs a diagonal banner or a starburst — never both; red text on yellow only at display sizes; no pastels, no minimalism; wear the edges, never the type.',
    preview: {
      bg: '#F2C744',
      surface: '#E8D8A8',
      text: '#12494D',
      accent: '#C1272D',
      font: 'display',
      radius: 0,
      border: '3px solid #12494D',
    },
  },
  {
    id: 'standards-manual',
    name: 'Standards Manual',
    category: 'editorial',
    tagline: 'Identity-manual precision with the grid exposed.',
    prompt:
      'Palette: white #FFFFFF field, black #101010 ink, one federal accent — NASA red #FC3D21 (alt: federal blue #105BD8) — reserved exclusively for identity moments: logotype, active states, chapter numbers; annotation gray #8E8E8E for measurement notes. Typography: Inter (fallback: strict Helvetica-class grotesque) on an unbreakable type scale; IBM Plex Mono for spec callouts and dimension labels; chapter numbering (1.1, 1.2) on every section heading. Shape: exposed structure — faint baseline grids and margin guides left visible as decoration; measurement annotations (arrows, x-height ticks) beside real components; generous Swiss margins; 0px radius. Texture/Effects: none; the grid overlay is the only ornament. Motion: ruler-precise 150ms slides that land exactly on the grid. Rules: gray annotates, never decorates; the accent never appears twice in one viewport region; no shadows, no gradients, no radius; every layout must survive with the grid overlay switched on.',
    preview: {
      bg: '#FFFFFF',
      surface: '#FFFFFF',
      text: '#101010',
      accent: '#FC3D21',
      font: 'sans',
      radius: 0,
      border: '1px solid #101010',
    },
  },
  {
      id: 'fintech-trust',
      name: 'Fintech Trust',
      category: 'product',
      tagline: 'Whitespace precision that reads like a balance sheet.',
      prompt:
        'Palette: pure white #FFFFFF canvas, deep navy ink #061B31, one accent #635BFF reserved exclusively for interactive elements — links, buttons, focus rings; green #16A34A and red #DC2626 appear only as transaction semantics. Typography: Inter for UI and headings, weight 600 maximum (IBM Plex Sans alternate; system sans fallback); JetBrains Mono with tabular numerals for every amount, ID, and account number — money is never set in the UI font. Shape: 8px radius on cards and inputs, hairline 1px #E5E7EB borders, two stacked low-alpha shadows instead of one heavy drop. Base spacing unit 8px; generous whitespace is the trust signal. Texture/Effects: none — no gradients or illustration anywhere near the transaction path. Motion: fast 150ms ease-outs; skeletons, never spinners. Rules: never use the accent as body text; never exceed weight 600; no pure black; tables ruled with hairlines, never zebra stripes.',
      preview: {
        bg: '#FFFFFF',
        surface: '#FFFFFF',
        text: '#061B31',
        accent: '#635BFF',
        font: 'sans',
        radius: 8,
        border: '1px solid #E5E7EB',
        shadow: '0 1px 2px rgba(6,27,49,0.06), 0 4px 12px rgba(6,27,49,0.05)',
      },
    },
    {
      id: 'luxury-noir',
      name: 'Luxury Noir',
      category: 'product',
      tagline: 'Black, champagne gold, and unhurried silence.',
      prompt:
        'Palette: near-black #0A0A0A canvas, bone #F5F1E8 text, champagne gold #C9A96E reserved exclusively for hairline rules, letterspaced labels, and the single call-to-action per view. Typography: Cormorant Garamond headlines at light weights — never bold the serif; Jost in uppercase with 0.2em tracking for labels and navigation; Jost light for body (Playfair Display alternate; classic serif fallback). Shape: 0–2px radius, thin 1px gold rules as the only dividers, no cards — content floats in enormous margins. Texture/Effects: subtle film grain over full-bleed imagery zones; no shadows anywhere. Motion: slow 600ms fades and gentle parallax; nothing snaps or bounces. Rules: one gold CTA per screen, never two; no drop shadows, no gradients, no pill buttons; never use pure white — bone only; whitespace is the luxury, never fill it.',
      preview: {
        bg: '#0A0A0A',
        surface: '#141414',
        text: '#F5F1E8',
        accent: '#C9A96E',
        font: 'serif',
        radius: 0,
        border: '1px solid #C9A96E',
      },
    },
    {
      id: 'healthcare-calm',
      name: 'Healthcare Calm',
      category: 'product',
      tagline: 'Soft clinical light that lowers the pulse.',
      prompt:
        'Palette: warm off-white #F9F7F4 canvas, white #FFFFFF cards, soft slate ink #26343D, calm teal #0F766E reserved exclusively for actions and progress; coral #F87171 appears only on alerts, never as decoration. Typography: Nunito Sans for headings and body (Source Sans 3 alternate; humanist sans fallback), body 16px minimum at 1.7 line-height. Shape: 12px radius, soft diffuse shadows — large blur, very low alpha — and roomy 8px-grid spacing with 24px card padding. Texture/Effects: flat color only; no gradients, no cold blue-gray chill, no photos of medical hardware. Motion: soothing 250–350ms ease-in-out fades; never bouncy, never springy. Rules: coral belongs to alerts exclusively; every tap target 44px or larger; break dense data into cards, never cram tables; body text never below 16px or under 4.5:1 contrast.',
      preview: {
        bg: '#F9F7F4',
        surface: '#FFFFFF',
        text: '#26343D',
        accent: '#0F766E',
        font: 'sans',
        radius: 12,
        shadow: '0 8px 24px rgba(38,52,61,0.08)',
      },
    },
    {
      id: 'kids-play',
      name: 'Kids Play',
      category: 'product',
      tagline: 'Storybook color with press-down 3D buttons.',
      prompt:
        'Palette: white #FFFFFF canvas, friendly ink #3C3C3C, owl green #58CC02 reserved exclusively for go actions and correct answers, sunshine #FFC800 for rewards and streaks, macaw blue #1CB0F6 for info and secondary chips. Typography: Baloo 2 for headings and buttons (Fredoka alternate; rounded display fallback), Nunito body. Shape: 16–20px radius everywhere; buttons are pills with a hard 4px darker bottom edge (e.g. #46A302 under the green) that collapses on press — a physical 3D push, never a blurred shadow; 2px #E5E5E5 borders on cards. Texture/Effects: flat sticker-style illustration, confetti bursts on wins. Motion: springy bounces, squash-on-tap, celebration moments on completion. Rules: green means go and nothing else; no thin weights, no gray-on-gray; no sharp corners anywhere; every empty state gets a friendly character, never a blank table.',
      preview: {
        bg: '#FFFFFF',
        surface: '#FFFFFF',
        text: '#3C3C3C',
        accent: '#58CC02',
        font: 'display',
        radius: 18,
        border: '2px solid #E5E5E5',
        shadow: '0 4px 0 #E5E5E5',
      },
    },
    {
      id: 'edtech-friendly',
      name: 'Friendly EdTech',
      category: 'product',
      tagline: 'Approachable indigo learning with visible progress.',
      prompt:
        'Palette: white #FFFFFF canvas, near-black ink #1E1E2A, indigo #4255FF reserved exclusively for interactive elements and progress fills, yellow #FDE047 as a text highlighter behind key phrases only — never on buttons; light indigo tint panels #F0F1FF for callouts. Typography: Poppins 600 for headings (Quicksand for a softer variant), Inter body; system sans fallback. Shape: 12px radius; lessons are cards with a visible progress bar; 1px #E8E8F0 borders, shadow only on hover. Base spacing 8px. Texture/Effects: flat color with tint panels; nothing heavier than a tint — no gradients. Motion: progress bars animate on load, streak counters tick up, 200ms ease-outs elsewhere. Rules: every screen shows progress somewhere; yellow is a highlighter, never a fill; never more than two accent colors in one view; the next action never hides below the fold.',
      preview: {
        bg: '#FFFFFF',
        surface: '#F0F1FF',
        text: '#1E1E2A',
        accent: '#4255FF',
        font: 'sans',
        radius: 12,
        border: '1px solid #E8E8F0',
      },
    },
    {
      id: 'neon-hud',
      name: 'Neon HUD',
      category: 'product',
      tagline: 'Clipped corners, glowing frames, tactical glass.',
      prompt:
        'Palette: void #0A0A12 canvas, ice text #DCF3FA, cyan #00F0FF reserved exclusively for interactive and live elements, magenta #FF2E88 for alerts and damage, amber #FFB020 for warnings. Typography: Orbitron for display in uppercase (Rajdhani alternate), Chakra Petch for body and data (squared sans fallback); numerals large and tabular. Shape: 45° clipped corners via clip-path instead of radius, 1px glowing #00F0FF borders on active panels, a faint grid overlay on the canvas. Texture/Effects: bloom glow around accents, scanline sweeps across hero panels, a touch of chromatic aberration on hover — sparingly. Motion: fast 120–180ms slide-ins, counters that spin up, scanline sweeps on state change. Rules: no rounded corners — clip, never round; glow shadows only, never soft black shadows; cyan means interactive and nothing else; body text stays glow-free for legibility.',
      preview: {
        bg: '#0A0A12',
        surface: '#101020',
        text: '#DCF3FA',
        accent: '#00F0FF',
        font: 'display',
        radius: 0,
        border: '1px solid #00F0FF',
        shadow: '0 0 12px rgba(0,240,255,0.4)',
      },
    },
    {
      id: 'web3-glow',
      name: 'Web3 Glow',
      category: 'product',
      tagline: 'Dark glass, aurora orbs, mint gains.',
      prompt:
        'Palette: deep space #0B0B1E canvas, text #EDEDF7, one violet-to-teal gradient #7C3AED→#06B6D4 for hero moments and primary CTAs only, mint #34D399 exclusively for gains, red #F87171 exclusively for losses. Typography: Space Grotesk display, Inter body (system sans fallback), JetBrains Mono for addresses and hashes — always truncated middle-out. Shape: 16px glass cards — rgba(255,255,255,0.06) fills, 1px rgba(255,255,255,0.12) borders, backdrop blur. Texture/Effects: two or three large blurred gradient orbs drifting behind content; neon glow on hover for interactive cards. Motion: ambient slow orb drift, ticker scrolls, 200ms hover glows. Rules: the gradient lives on CTAs and the hero — never on body text or every border; green and red are semantic, never decorative; no pure black; never blur text itself.',
      preview: {
        bg: '#0B0B1E',
        surface: 'rgba(255,255,255,0.06)',
        text: '#EDEDF7',
        accent: '#7C3AED',
        font: 'sans',
        radius: 16,
        border: '1px solid rgba(255,255,255,0.12)',
      },
    },
    {
      id: 'data-dense',
      name: 'Data Dense',
      category: 'product',
      tagline: 'Bloomberg density — every pixel earns its rent.',
      prompt:
        'Palette: near-black #111315 canvas, panel #191C1F, text #E0E3E0, amber #FFB000 for highlights and active states, signal green #4AF6C3 for up-moves, red #FF433D for down-moves and alerts. Typography: IBM Plex Mono for every numeral, ID, and code; condensed 11–13px labels in IBM Plex Sans Condensed (Saira Condensed alternate; condensed sans fallback), uppercase column headers. Shape: 0–2px radius, ruled 1px #2A2E33 table lines, minimal 4px cell padding, inline sparklines beside figures. Texture/Effects: none — the data is the chrome. Motion: only data-change flashes — a cell blinks amber, green, or red by direction for 300ms on update; nothing else animates. Rules: density beats whitespace here — never add air to tables; amber, green, and red are semantic only, never decoration; no card shadows, no gradients; table text never above 13px.',
      preview: {
        bg: '#111315',
        surface: '#191C1F',
        text: '#E0E3E0',
        accent: '#FFB000',
        font: 'mono',
        radius: 2,
        border: '1px solid #2A2E33',
      },
    },
    {
      id: 'civic-plain',
      name: 'Civic Plain',
      category: 'product',
      tagline: 'Radical plainness a citizen can trust.',
      prompt:
        'Palette: white #FFFFFF canvas, near-black text #0B0C0C, link blue #1D70B8, action green #00703C exclusively for primary buttons, focus yellow #FFDD00 on every focused element. Typography: Public Sans (Source Sans 3 alternate; system sans fallback), large 19px body, headings bold and flush-left. Shape: 0 radius everywhere — no rounded corners; 2px solid #0B0C0C borders on inputs; single-column forms, one question per screen where possible. Texture/Effects: none. No shadows, no gradients, no icon where a word will do. Motion: none beyond instant state changes; focus visibility never depends on animation. Rules: green buttons carry the one primary action per page, never more; every focusable element gets a 3px #FFDD00 focus outline; never center form labels; never convey meaning by color alone; no decorative imagery.',
      preview: {
        bg: '#FFFFFF',
        surface: '#FFFFFF',
        text: '#0B0C0C',
        accent: '#00703C',
        font: 'sans',
        radius: 0,
        border: '2px solid #0B0C0C',
      },
    },
    {
      id: 'hyperlegible',
      name: 'Hyperlegible',
      category: 'product',
      tagline: 'Contrast and size as the whole aesthetic.',
      prompt:
        'Palette: white #FFFFFF canvas, ink #1A1A1A held at 7:1+ contrast, accent #005A9C reserved exclusively for links and actions, focus #FFBF47 outlines on everything focusable. Typography: Atkinson Hyperlegible for body (Lexend alternate; humanist sans fallback), 18px+ body at 1.6 line-height, headings step up in clear bold jumps. Shape: 4px radius, 2px solid borders on all controls, 44px minimum touch targets, visibly distinct hover/focus/active/disabled states — disabled is a gray fill, never lowered opacity. Texture/Effects: none; flat surfaces only. Motion: reduced-motion-first — default to instant state changes, opt into 150ms fades only when the user allows motion. Rules: text contrast never below 7:1; never encode meaning in color alone — pair with icon or label; no placeholder-as-label inputs; no hover-only affordances.',
      preview: {
        bg: '#FFFFFF',
        surface: '#FFFFFF',
        text: '#1A1A1A',
        accent: '#005A9C',
        font: 'sans',
        radius: 4,
        border: '2px solid #1A1A1A',
      },
    },
    {
      id: 'commerce-bold',
      name: 'Commerce Bold',
      category: 'product',
      tagline: 'High-energy retail built to convert.',
      prompt:
        'Palette: white #FFFFFF canvas, near-black #111111 ink, hot orange #FF6B35 reserved exclusively for add-to-cart and checkout, sale red #DC2626 only for price cuts and urgency badges. Typography: Archivo Black for headlines and prices (Oswald alternate; heavy display fallback), Inter body; prices always bold and larger than product names. Shape: 8px cards in dense product grids, full-width pill CTAs on mobile, ratings and badges inline under every title. Texture/Effects: photography does the decorating — the chrome stays flat; solid-fill badge chips. Motion: hover zoom on product images (scale 1.05), cart slide-over from the right, count-ups on totals — everything under 250ms. Rules: one orange CTA per card, and orange never touches anything non-buyable; red only ever means sale or urgency; never crop product photos to odd ratios; never set a price in thin gray type.',
      preview: {
        bg: '#FFFFFF',
        surface: '#FFFFFF',
        text: '#111111',
        accent: '#FF6B35',
        font: 'display',
        radius: 8,
        shadow: '0 2px 8px rgba(17,17,17,0.10)',
      },
    },
    {
      id: 'legal-gravitas',
      name: 'Legal Gravitas',
      category: 'product',
      tagline: 'Navy, ivory, and Caslon authority.',
      prompt:
        'Palette: deep navy #1B2A4A for the canvas and header bands, ivory #F8F5EF surfaces, navy ink on ivory, muted gold #A3854E reserved exclusively for rules, small-caps labels, and the single primary action. Typography: Libre Caslon Text headings (EB Garamond alternate; old-style serif fallback), Source Serif 4 body, letterspaced small-caps labels; the serif never exceeds weight 600. Shape: 0–4px radius, ruled 1px dividers, centered symmetric layouts with generous top margins; no cards floating on shadows. Texture/Effects: none — flat ivory and navy; a thin double rule under section titles is the only ornament. Motion: fades only, 300ms; nothing slides, nothing bounces. Rules: gold never appears as body text or a background; hold line length at 65–75 characters; no sans-serif headlines; never pure white, never pure black.',
      preview: {
        bg: '#1B2A4A',
        surface: '#F8F5EF',
        text: '#1B2A4A',
        accent: '#A3854E',
        font: 'serif',
        radius: 2,
        border: '1px solid #A3854E',
      },
    },
    {
      id: 'jet-age',
      name: 'Jet Age',
      category: 'product',
      tagline: 'Mid-century boarding-pass optimism.',
      prompt:
        'Palette: cream #F6F1E7 canvas, aviation navy #153A5B ink and section bands, cherry red #D64541 reserved exclusively for primary actions and route markers, sky teal #2AA5A0 for secondary accents. Typography: Jost in confident caps for headings (Poppins Bold alternate; geometric sans fallback), Jost body; ticket details in Space Mono like a printed stub. Shape: 12px boarding-pass cards with a dashed perforation divider between summary and detail; roundel badges and horizontal stripe motifs on bands. Texture/Effects: subtle poster grain on hero bands; flat mid-century illustration, never photography. Motion: horizontal slide transitions like a departures board; a small plane-path line animates progress. Rules: red belongs to actions and markers only; no gradients, no glassmorphism; shadows never heavier than 2px; radius is 12px everywhere — never mix corner treatments.',
      preview: {
        bg: '#F6F1E7',
        surface: '#FFFFFF',
        text: '#153A5B',
        accent: '#D64541',
        font: 'sans',
        radius: 12,
        border: '1px dashed #153A5B',
      },
    },
    {
      id: 'menu-chic',
      name: 'Menu Chic',
      category: 'product',
      tagline: 'The menu is the interface.',
      prompt:
        'Palette: warm paper #F7F3EC canvas, espresso ink #2B2320, olive #6B7245 reserved exclusively for prices, links, and the reserve action (burgundy #6E2B2B is the acceptable alternative accent — pick one, never both). Typography: Cormorant for dish and section titles (DM Serif Display alternate; classic serif fallback), Karla in all-caps with 0.15em tracking for category labels, Karla body. Shape: no cards — one centered column ruled by hairlines; dotted leader lines connect each item to its right-aligned price; 0–2px radius on the rare control. Texture/Effects: paper warmth only; no shadows, no imagery inside the menu column. Motion: none — the page holds still; at most a 200ms fade between courses. Rules: never box menu items into cards; prices always right-aligned on the dotted leader; one accent color total; no dish photography inside lists.',
      preview: {
        bg: '#F7F3EC',
        surface: '#F7F3EC',
        text: '#2B2320',
        accent: '#6B7245',
        font: 'serif',
        radius: 0,
        border: '1px solid rgba(43,35,32,0.25)',
      },
    },
    {
      id: 'stadium-shout',
      name: 'Stadium Shout',
      category: 'product',
      tagline: 'Condensed caps, diagonal cuts, volt energy.',
      prompt:
        'Palette: white #FFFFFF and black #0A0A0A in bold alternating blocks, volt #DFFF00 reserved exclusively for CTAs, live states, and personal records; infrared #FF3B30 only for alerts. Typography: Anton in ALL CAPS with tight tracking for headlines (Archivo Black alternate; condensed display fallback), Inter body; stat numerals oversized at 80px+ and tabular. Shape: 0–4px radius, sections sliced with 12–18° diagonal clip-path cuts, stat blocks running edge-to-edge. Texture/Effects: high-contrast duotone photography in black and volt; no gradients, no soft shadows. Motion: fast skewed slide-ins, kinetic type snapping into place, count-ups on stats — 150–250ms with aggressive easing. Rules: volt sits on black or black on volt — never volt on white; headlines never lowercase; no rounded pills; every hero leads with a number or a verb.',
      preview: {
        bg: '#FFFFFF',
        surface: '#0A0A0A',
        text: '#FFFFFF',
        accent: '#DFFF00',
        font: 'display',
        radius: 0,
      },
    },
    {
      id: 'midnight-stage',
      name: 'Midnight Stage',
      category: 'product',
      tagline: 'Spotify dark meets the club flyer.',
      prompt:
        'Palette: charcoal #121212 canvas, elevated surfaces #1E1E1E, text #F2F2F2, stage green #1DB954 reserved exclusively for play, live, and interactive states; magenta #E91E8C only for special-event moments. Typography: Archivo Black oversized for artist and event names (Unbounded for stunt headlines; heavy display fallback), Inter for UI. Shape: 8px media cards, edge-to-edge artwork rows, sticker-style genre chips. Texture/Effects: blurred album-art color bleeding behind the hero, duotone treatments over photos, waveform strips as progress bars. Motion: marquee scroll on overflowing titles, crossfades between artwork, 200ms hover lifts. Rules: green means play and nothing else; the artwork supplies the color — chrome stays gray; never set text on artwork without a scrim; no light mode.',
      preview: {
        bg: '#121212',
        surface: '#1E1E1E',
        text: '#F2F2F2',
        accent: '#1DB954',
        font: 'display',
        radius: 8,
      },
    },
    {
      id: 'terracotta-earth',
      name: 'Terracotta Earth',
      category: 'mood',
      tagline: 'Clay, sand, and moss — grounded and warm.',
      prompt:
        'Palette: sand #E5D5B7 canvas, lighter sand #F0E5CF panels, soil ink #5C4033, terracotta #E2725B reserved exclusively for primary actions and links, moss #606C38 for secondary accents and tags. Typography: Fraunces headlines (soft high-contrast serif fallback), Work Sans body, Courier Prime for small annotations and captions. Shape: 12–20px radius, organic blob and arch masks on imagery, layered sand-on-sand tint panels instead of shadows — no hard shadows anywhere. Base spacing 8px with generous padding. Texture/Effects: paper grain across the canvas, hand-drawn line icons, no glossy gradients. Motion: unhurried 300–450ms eases; elements settle, never bounce. Rules: terracotta appears only on buttons and links; never pure white or pure black — every neutral is warmed; no drop shadows — layer tints instead; photography must be natural light, never studio-blue.',
      preview: {
        bg: '#E5D5B7',
        surface: '#F0E5CF',
        text: '#5C4033',
        accent: '#E2725B',
        font: 'serif',
        radius: 16,
      },
    },
    {
      id: 'botanical',
      name: 'Botanical',
      category: 'mood',
      tagline: 'Lush leaf greens and watercolor freshness.',
      prompt:
        'Palette: cream #FEFAE0 canvas, deep leaf ink #1B4332, leaf green #2D6A4F for headings and structure, fern #95D5B2 as tint panels and borders, marigold #FCA311 reserved exclusively for calls to action. Typography: Lora headlines with Fraunces italic accents (serif fallback), Nunito Sans body. Shape: rounded 16px cards, arched image masks, thin 1px leaf-line borders; feathered shadows at very low alpha only. Texture/Effects: watercolor foliage washes in corners and at section breaks — decoration lives at the edges, never behind text. Motion: gentle sway on decorative leaves, grow-in reveals on scroll, 300ms eases. Rules: marigold is the only warm color and appears only on actions; foliage stays decorative — never behind body copy; no gray — neutrals are cream and green-tinted; never use neon greens.',
      preview: {
        bg: '#FEFAE0',
        surface: '#FFFFFF',
        text: '#1B4332',
        accent: '#FCA311',
        font: 'serif',
        radius: 16,
        border: '1px solid #95D5B2',
      },
    },
    {
      id: 'deep-sea',
      name: 'Deep Sea',
      category: 'mood',
      tagline: 'From sunlit foam down to the abyss.',
      prompt:
        'Palette: abyssal navy #03045E depths, current blue #0077B6 mid-waters, biolume cyan #48CAE4 reserved exclusively for interactive elements, foam #CAF0F8 text. Structure the page as a descent — the background deepens from #0077B6 toward #03045E as the user scrolls. Typography: Comfortaa for rounded display headings (Quicksand alternate; rounded sans fallback), Inter body. Shape: 16px radius, translucent layered panels of low-alpha foam, SVG wave dividers between sections. Texture/Effects: faint caustic light patterns near the surface, drifting particle specks in the depths, soft glow on cyan elements. Motion: slow parallax drift; elements rise with buoyant easing — slight overshoot, soft settle. Rules: cyan is interactive only; text is always foam-on-deep, never dark-on-dark; sections break on waves, never straight cuts; no warm colors anywhere.',
      preview: {
        bg: '#03045E',
        surface: '#0077B6',
        text: '#CAF0F8',
        accent: '#48CAE4',
        font: 'sans',
        radius: 16,
      },
    },
    {
      id: 'desert-dusk',
      name: 'Desert Dusk',
      category: 'mood',
      tagline: 'Burnt sienna horizons and long shadows.',
      prompt:
        'Palette: sand #F4E4C1 canvas, dusk rose #E8A08D tint panels, night indigo #2D3561 ink, burnt sienna #C1440E reserved exclusively for primary actions. Typography: DM Serif Display headlines (Prata alternate; high-contrast serif fallback), Karla body. Shape: horizon-band layouts — wide horizontal sections stacked like a landscape; sun-disc circular motifs, arched masks on imagery, 8–12px radius. Texture/Effects: fine grain over hero bands; long, soft, single-direction shadows as if lit from the horizon — used sparingly. Motion: colors warm subtly as the page scrolls, a slow sunset shift; 300ms fades elsewhere. Rules: sienna appears only on actions; shadows fall in one direction everywhere — never mixed light sources; gradients exist only as sky bands in heroes, never on controls; ink stays indigo, never black.',
      preview: {
        bg: '#F4E4C1',
        surface: '#E8A08D',
        text: '#2D3561',
        accent: '#C1440E',
        font: 'serif',
        radius: 10,
      },
    },
    {
      id: 'cabin-fire',
      name: 'Cabin Fire',
      category: 'mood',
      tagline: 'Dark timber, firelight amber, wool comfort.',
      prompt:
        'Palette: pine #1B3022 canvas, timber #6F4E37 plank panels, cream #F3EAD8 text, amber #E8A853 reserved exclusively for actions and highlights — amber is the firelight. Typography: Bitter for headings (Zilla Slab alternate; slab-serif fallback), Source Sans 3 body. Shape: 8–12px radius, wide plank-like cards with 20px+ padding, stitched 1px dashed #6F4E37 dividers. Texture/Effects: subtle wood-grain tint on panels; focused elements get a warm amber glow — glow, never a gray shadow. Motion: soft and slow, 300–500ms; hovers brighten like stoked embers. Rules: amber is the only bright — never introduce a cool color; text is cream, never pure white; no crisp black shadows — warm glows only; imagery stays warm-toned and grainy, never clinical.',
      preview: {
        bg: '#1B3022',
        surface: '#6F4E37',
        text: '#F3EAD8',
        accent: '#E8A853',
        font: 'serif',
        radius: 10,
        border: '1px dashed #8A6A50',
      },
    },
    {
      id: 'cosmos',
      name: 'Cosmos',
      category: 'mood',
      tagline: 'Starfield wonder with orbital calm.',
      prompt:
        'Palette: void #0B0D17 canvas, star white #F8F8FF text, nebula violet #6C3FC5 for structure and secondary fills, pulsar cyan #4CC9F0 reserved exclusively for interactive elements. Typography: Space Grotesk display (Exo 2 alternate; geometric sans fallback), Inter body, tabular numerals for data readouts. Shape: 12px glass cards — rgba(255,255,255,0.04) fills with 1px rgba(255,255,255,0.10) borders — floating over a sparse starfield; thin orbit-ring circles frame key stats. Texture/Effects: tiny static star specks and one faint violet nebula gradient per screen, never behind body text. Motion: extremely slow parallax on the starfield, elements orbit into place on load, 250ms eases in the UI. Rules: cyan means interactive, violet never does; one nebula per viewport; never pure black — the void carries blue; glow borders on hover only.',
      preview: {
        bg: '#0B0D17',
        surface: 'rgba(255,255,255,0.04)',
        text: '#F8F8FF',
        accent: '#4CC9F0',
        font: 'sans',
        radius: 12,
        border: '1px solid rgba(255,255,255,0.10)',
      },
    },
    {
      id: 'arctic-glass',
      name: 'Arctic Glass',
      category: 'mood',
      tagline: 'Frosted panels in glacier light.',
      prompt:
        'Palette: glacier #F4F9FC canvas, fjord ink #1D3557 — fjord doubles as the interactive color — ice blue #A8DADC for tints and secondary fills, frost panels of rgba(255,255,255,0.6) with backdrop blur. Typography: Manrope at light weights with wide tracking (Sora alternate; airy sans fallback); nothing bolder than 600. Shape: frosted glass cards at 16–20px radius, 1px rgba(255,255,255,0.8) light borders catching the edge, generous cool whitespace. Texture/Effects: a soft shimmer highlight sweeping across one hero panel; faint crystalline linework as decoration. Motion: glide easing — long 300–400ms ease-outs — with a gentle shimmer on hover. Rules: everything stays cold — no warm hue anywhere; blur backs panels, never text; shadows are barely-there cool tints, never black; keep type light — no weight above 600.',
      preview: {
        bg: '#F4F9FC',
        surface: 'rgba(255,255,255,0.6)',
        text: '#1D3557',
        accent: '#1D3557',
        font: 'sans',
        radius: 18,
        border: '1px solid rgba(255,255,255,0.8)',
        shadow: '0 8px 24px rgba(29,53,87,0.08)',
      },
    },
    {
      id: 'candy-pop',
      name: 'Candy Pop',
      category: 'mood',
      tagline: 'Bubblegum, mint, and butter — sugar-rush soft.',
      prompt:
        'Palette: blush canvas #FFF0F6, white cards, charcoal ink #3A3A3A — never pure black; bubblegum #FFC6E0, mint #B8F1D9, and butter #FFF3B0 rotate as pastel fills; grape #B79CED reserved exclusively for primary actions. Typography: Baloo 2 for bubbly headings (Fredoka alternate; rounded display fallback), DM Sans body. Shape: 20–24px radius, pill buttons, blob decorations, sticker-style cards with a small hard offset shadow in a darker candy tone (3px 3px 0 #B79CED) — never a gray blur. Texture/Effects: sprinkle dots and star stickers as accents; flat candy fills, no gradients. Motion: bouncy springs, squish on press (scale 0.95), a playful wiggle on chip hover. Rules: grape owns the CTA; never more than three pastels in one view; shadows are candy-colored offsets, never black blurs; no sharp corners, no gray.',
      preview: {
        bg: '#FFF0F6',
        surface: '#FFFFFF',
        text: '#3A3A3A',
        accent: '#B79CED',
        font: 'display',
        radius: 22,
        shadow: '3px 3px 0 #FFC6E0',
      },
    },
    {
      id: 'cyber-noir',
      name: 'Cyber Noir',
      category: 'mood',
      tagline: 'Rain-slick neon in the megacity dark.',
      prompt:
        'Palette: asphalt #0D0B14 canvas, fog text #E6E1F0, neon pink #FF2A6D reserved exclusively for primary actions and live signals, cyan #05D9E8 for links and data, violet haze #7A04EB for ambient glows only. Typography: Rajdhani for display (Michroma for logo-scale moments; squared techno fallback), Inter body. Shape: sharp 0–4px corners, 1px neon-border panels on near-black fills, headings styled like glowing signage with a text-shadow bloom in the accent. Texture/Effects: fine grain and faint vertical rain streaks on hero backdrops, layered fog gradients at section edges, a VHS-style glitch on hover — one glitching element per viewport, maximum. Motion: neon flicker-on as headings enter, 150ms glitch transitions, slow drifting fog. Rules: pink is action, cyan is information — never swap them; glows replace shadows entirely; no light mode; body text never glows.',
      preview: {
        bg: '#0D0B14',
        surface: '#151020',
        text: '#E6E1F0',
        accent: '#FF2A6D',
        font: 'display',
        radius: 2,
        border: '1px solid #FF2A6D',
        shadow: '0 0 12px rgba(255,42,109,0.35)',
      },
    },
    {
      id: 'solarpunk',
      name: 'Solarpunk',
      category: 'mood',
      tagline: 'Art-Nouveau curves for a green-tech future.',
      prompt:
        'Palette: cream #FBF6E9 canvas, deep green ink #2C3B2D, canopy green #3A7D44 for structure and borders, sky #7EC8E3 for tints, sun gold #F2C14E reserved exclusively for primary actions. Typography: Philosopher for headlines (Cormorant alternate; humanist serif fallback), Mulish body. Shape: the arch is the signature — tops of cards and images curve into arched windows; vine-like 1px borders, stained-glass divider motifs, 16px radius below the arch. Texture/Effects: botanical engraving line-art in margins, soft light-ray gradients from top corners — decoration frames content, never covers it. Motion: sun-path arc reveals, leaf-growth progress indicators, 300ms organic eases. Rules: gold appears only on actions; use arches wherever a rectangle would do — but never on data tables; no dark mode — this future is bright; decorate with line-art only, never stock photography.',
      preview: {
        bg: '#FBF6E9',
        surface: '#FFFFFF',
        text: '#2C3B2D',
        accent: '#F2C14E',
        font: 'serif',
        radius: 16,
        border: '1px solid #3A7D44',
      },
    },
    {
      id: 'steel-yard',
      name: 'Steel Yard',
      category: 'mood',
      tagline: 'Concrete, graphite, and safety orange.',
      prompt:
        'Palette: concrete #B5B5AF canvas, lighter slab panels #C6C6C0, graphite ink #3A3A38, safety orange #FF6600 reserved exclusively for actions and hazards. Typography: Saira Condensed in caps for headings (Oswald alternate; condensed sans fallback), IBM Plex Sans body, IBM Plex Mono for specs, IDs, and dimensions. Shape: 0 radius, 2px solid #3A3A38 borders, visible plated seams — sections butt together along rules, nothing floats; dense ruled spec tables. Texture/Effects: faint concrete grain on the canvas; orange-and-graphite hazard-stripe bands on warnings only. Motion: mechanical linear slides at 150ms, no bounce, no overshoot — machines do not bounce. Rules: orange means action or hazard, nothing else; no rounded corners, no shadows — borders do all separation; no photo filters — flat, honest imagery; never soften the grid.',
      preview: {
        bg: '#B5B5AF',
        surface: '#C6C6C0',
        text: '#3A3A38',
        accent: '#FF6600',
        font: 'sans',
        radius: 0,
        border: '2px solid #3A3A38',
      },
    },
    {
      id: 'paper-zen',
      name: 'Paper Zen',
      category: 'mood',
      tagline: 'Washi paper, sumi ink, one vermilion seal.',
      prompt:
        'Palette: washi #F7F4EE canvas, sumi ink #2A2A28, vermilion #D64533 reserved exclusively for the single most important action per screen — placed like a hanko seal. Typography: Noto Serif at light weights (quiet serif fallback), body 17px+ at 1.9 line-height; two weights maximum per screen. Shape: no cards, no boxes — content is separated by vast negative space and the rare hairline rule; 0–2px radius on the few controls that exist. Texture/Effects: barely-visible paper fiber on the canvas; at most one brushstroke graphic per page. Motion: a single slow 400ms fade per view; nothing else moves. Rules: one vermilion element per screen, never two; no shadows, no borders around content, no icon where whitespace can separate; never bold the serif; emptiness is the layout — do not fill it.',
      preview: {
        bg: '#F7F4EE',
        surface: '#F7F4EE',
        text: '#2A2A28',
        accent: '#D64533',
        font: 'serif',
        radius: 0,
      },
    },
    {
      id: 'hygge',
      name: 'Hygge',
      category: 'mood',
      tagline: 'Candlelight neutrals and pillowy calm.',
      prompt:
        'Palette: oat #F0E9DF canvas, cream surfaces #FAF6EF, charcoal ink #403A35, mushroom #B8A99A for secondary text and hairlines, rust #B2604B reserved exclusively for actions. Typography: Sen for headings (soft sans fallback), Nunito body, Lora italic for warm asides and quotes. Shape: 16px radius, pillowy diffuse shadows — very large blur, warm-tinted, low alpha — and thick breathing room with 32px minimum card padding. Texture/Effects: a subtle wool or linen weave on large empty areas; photography soft and lamp-lit. Motion: warm 300ms fades; hovers glow gently brighter like lamplight — elements never lift or pop. Rules: rust appears only on buttons and links; shadows are warm gray, never cool or black; no sharp corners, no pure white; contrast stays gentle everywhere except body text, which remains fully readable.',
      preview: {
        bg: '#F0E9DF',
        surface: '#FAF6EF',
        text: '#403A35',
        accent: '#B2604B',
        font: 'sans',
        radius: 16,
        shadow: '0 12px 32px rgba(64,58,53,0.10)',
      },
    },
    {
      id: 'spa-still',
      name: 'Spa Still',
      category: 'mood',
      tagline: 'Sage, sand, and stone — exhale slowly.',
      prompt:
        'Palette: sand #E7DED2 canvas, soft espresso ink #3E3832, sage #9CAF88 reserved exclusively for actions and active states, stone #8A8477 for secondary text, clay #C67B5C sparingly for warm highlights. Typography: Cormorant at light weights for headlines — never bold the serif; Karla body with comfortable lowercase labels and navigation (humanist sans fallback). Shape: 24px+ radius, arch and oval masks on imagery, no borders anywhere; feathered, barely-there shadows. Texture/Effects: linen grain on the canvas, watercolor washes at section edges. Motion: breathing fades at 400–800ms — inhale-exhale pacing; nothing snaps or bounces. Rules: sage is the only interactive color; never draw borders — separate with space and tone; no all-caps anywhere except tiny letterspaced section markers; never pure white or pure black.',
      preview: {
        bg: '#E7DED2',
        surface: '#EFE9DD',
        text: '#3E3832',
        accent: '#9CAF88',
        font: 'serif',
        radius: 24,
      },
    },
    {
      id: 'ink-mono',
      name: 'Ink Mono',
      category: 'mood',
      tagline: 'Two colors. Hover inverts. Nothing else.',
      prompt:
        'Palette: #FFFFFF and #111111 only — a third color does not exist; hover and selection invert the block to #111111 with white text. Typography: Fraunces at extreme display sizes (Inter Tight for a grotesque variant; high-contrast serif fallback), Inter body; sizes jump dramatically — 14px captions to 96px+ headlines with little in between. Shape: hairline 1px #111111 rules, no cards, no radius; links underlined; buttons are text blocks that invert on hover. Texture/Effects: black-and-white photography and halftone treatments only; no gray tints outside photographs. Motion: cursor flourishes, text-mask reveals on scroll, and instant hover inversion — the flip never fades. Rules: no color, ever — not even for errors (use weight and rules instead); no gray UI tones — pure black on pure white; no shadows, no gradients; inversion is the only hover state.',
      preview: {
        bg: '#FFFFFF',
        surface: '#FFFFFF',
        text: '#111111',
        accent: '#111111',
        font: 'display',
        radius: 0,
        border: '1px solid #111111',
      },
    },
    {
      id: 'gallery-concrete',
      name: 'Gallery Concrete',
      category: 'mood',
      tagline: 'Gallery white, giant type, hairline restraint.',
      prompt:
        'Palette: gallery white #FAFAF8 canvas, graphite #1F1F1F ink, warm gray #8C8880 for captions and rules; the accent is scale, not color — graphite doubles as the interactive tone. Typography: Inter Tight at architectural scale — 120px+ hero headlines (Archivo alternate; grotesque fallback) — against tiny 11px letterspaced Space Mono captions. Shape: a strict 12-column grid with visible discipline; no cards — hairline #E3E1DC rules divide; 0 radius; images sit flush to the grid. Texture/Effects: none on the chrome; large photography breathes inside whitespace; an optional cursor-following image reveal on project lists. Motion: smooth inertial scroll, subtle image parallax, 400ms reveals staggered down the grid. Rules: no color accents — restraint is the brand; no shadows, no rounded corners, no border thicker than a hairline; captions stay mono and small, headlines enormous — nothing mid-sized.',
      preview: {
        bg: '#FAFAF8',
        surface: '#FAFAF8',
        text: '#1F1F1F',
        accent: '#8C8880',
        font: 'sans',
        radius: 0,
        border: '1px solid #E3E1DC',
      },
    },
];
